import Screen from "../lib/Screen";

import * as PIXI from "pixi.js";
import * as PIXI3D from "pixi3d";
import gsap from "gsap";
import { IGameSnacks } from "../lib/GameSnacks";
import Player, { player_side_type } from "./game/Player";
import ServiceLocator from "../ServiceLocator";
import Utils from "../Utils";
import Session, { game_type } from "../Session";
import { LiveEventData, LivePlayerDataEvent, LivePresenceAction } from "../lib/game_snacks/ILiveMultiplayer";
import { screen_type } from "../state/GameState";
import Settings from "./Settings";
import TutorialPopup from "./popup/TutorialPopup";
import LeaveConfirmationPopup from "./popup/LeaveConfirmationPopup";
import { game_event_type } from "../service/MultiplayerEventManager";
import GameOverPopup from "./popup/GameOverPopup";
import YouWinPopup from "./popup/YouWinPopup";
import YouLosePopup from "./popup/YouLosePopup";
import DrawPopup from "./popup/DrawPopup";
import MessagePopup from "./popup/MessagePopup";
import SpriteMaskAnim from "../lib/SpriteMaskAnim";
import ConfettiAnim from "./ConfettiAnim";
import HandDevicePopup from "./popup/HandDevicePopup";
import { Profile } from "../lib/game_snacks/IUser";


declare global {
    interface Window { GameSnacks: IGameSnacks; }
}


export default class GameplayScreen extends Screen
{
    // #region Attributes //

    // private:

    private _layers: IGameLayers = null;

    private _bg: PIXI.Sprite = null;
    private _bgMaskAnim: SpriteMaskAnim = null;
    private _dartboardInner: PIXI.Sprite = null;
    private _dartboardInnerMaskAnim: SpriteMaskAnim = null;
    private _dartboardFrame: PIXI.Sprite = null;
    private _dartboardFrameMaskAnim: SpriteMaskAnim = null;
    private _arrUserBaseAnim: Array<PIXI.AnimatedSprite> = null;
    private _arrPlayer: Array<Player> = null;
    private _curPlayer: Player = null;
    private _myPlayer: Player = null;
    private _roundCount: number = null;
    private _scoreWrapper: PIXI.Container = null;
    private _scoreBase: PIXI.Sprite = null;
    private _scoreText: PIXI.Text = null;
    private _scoreMultBadge: PIXI.Sprite = null;
    private _checkoutBustBanner: PIXI.Sprite = null;
    private _bullsEyeBanner: PIXI.Sprite = null;
    private _remainingDartsWrapper: PIXI.Container = null;
    private _remainingDartsBase: PIXI.Sprite = null;
    private _remainingDartsTextWrapper: PIXI.Container = null;
    private _yourTurnBase: PIXI.Sprite = null;
    private _yourTurnText: PIXI.Text = null;
    private _roundBase: PIXI.Sprite = null;
    private _bannerBase: PIXI.Sprite = null;
    private _bannerMaskIn: SpriteMaskAnim = null;
    private _bannerMaskOut: SpriteMaskAnim = null;
    private _tutorialHand: PIXI.Sprite = null;

    private _dartMoveWrapper: PIXI3D.Container3D = null;
    private _dartRotYWrapper: PIXI3D.Container3D = null;
    private _dartBaseTransformWrapper: PIXI3D.Container3D = null;
    private _dartRotZWrapper: PIXI3D.Container3D = null;
    private _dart: PIXI3D.Model = null;
    private _arrScoredDartWrapper: Array<PIXI3D.Container3D> = null;
    private _dartHitAnim: PIXI.AnimatedSprite = null;

    private _targetSectorOutlineWrapper: PIXI3D.Container3D = null;
    private _sectorHighlightWrapper: PIXI3D.Container3D = null;
    private _sectorHighlightOverlay: PIXI3D.Sprite3D = null;
    private _sectorHighlightOutline: PIXI3D.Sprite3D = null;

    private _arrDartShadow: Array<PIXI3D.Sprite3D> = null;
    private _confettiAnim: ConfettiAnim = null;
    
    private _arrThrowFrom: Array<IThrowFrom> = null;
    private _aimingTimer: number = null;
    private _followDartTargetZ: number = null;

    private _gameOverPopup: GameOverPopup = null;

    private _throwDartTl: gsap.core.Timeline = null;
    private _followDartTl: gsap.core.Timeline = null;
    private _actionTextTl: gsap.core.Timeline = null;
    private _scoreTl: gsap.core.Timeline = null;
    //private _darkLayerTween: gsap.core.Tween = null;
    private _dartboardEntryTl: gsap.core.Timeline = null;
    private _sectorHighlightTl: gsap.core.Timeline = null;
    private _tutorialHandTl: gsap.core.Timeline = null;

    private _isNextAction: boolean = null;
    private _isPaused: boolean = null;

    private _settings: Settings = null;

    private _playerInactivityTimer: number = null;
    private _imAliveTimer: number = null;

    private _state: state_type = null;
    private _inputState: input_state_type = null;
    private _playerDartCount: number = null;
    private _playerRoundScore: number = null;

    private _arrMissingPlayerId: Array<number> = null;

    private _isKeepRoom: boolean = null;
    private _isRematchRequestReceived: boolean = null;

    private _gameRulesType: game_rules_type = null;

    private _arrSprayHowl: Array<Howl> = null;

    private readonly _kPixelToWorldUnits: number = 0.0099;
    private readonly _kDartMoveBaseY: number = -1.4;//-2;
    private readonly _kDartMoveBaseZ: number = -3;//-2;
    private readonly _kDartBaseY: number = -1;//-1;//-0.6;
    private readonly _kDartBaseZ: number = 1.4//;0.6;
    private readonly _kCameraBaseZ: number = 5;
    private readonly _kDartboardBaseY: number = 2.8;//4;
    private readonly _kDarboardDistanceZ: number = 20;
    private readonly _kDartEndRotationZ: number = -180;
    private readonly _kPlayerTimeLimit: number = 10;
    private readonly _kPlayerInactivityTimeLimit: number = 40;
    private readonly _kScoreBgBaseY: number = 60;
    private readonly _kScoreWrapperBaseY: number = -80;
    private readonly _kDartboardScale: number = 0.88;//0.75;


    // #endregion //


    // #region Methods //

    // public:

    public constructor()
    {
        super();
    }

    public onEnter(): void
    {
        super.onEnter();

        this._arrUserBaseAnim = [];
        this._arrPlayer = new Array<Player>();
        this._roundCount = 1;
        this._arrScoredDartWrapper = [];
        this._arrDartShadow = [];
        this._arrThrowFrom = [];
        this._aimingTimer = 0;
        this._isNextAction = false;
        this._isPaused = false;
        this._state = state_type.PLAYING;
        this._playerDartCount = 0;
        this._playerRoundScore = 0;
        this._arrMissingPlayerId = [];
        this._isKeepRoom = false;
        this._isRematchRequestReceived = false;
        this._gameRulesType = Session.i.gameType == game_type.CLASSIC_MATCHMAKING
            || Session.i.gameType == game_type.CLASSIC_OFFLINE
            || Session.i.gameType == game_type.CLASSIC_PRIVATE ? game_rules_type.CLASSIC : game_rules_type.T301;
        this._arrSprayHowl = [ null, null ];

        this._layers = {
            bg: new PIXI.Container(),
            intro: new PIXI.Container(),
            world: new PIXI.Container(),
            vfx: new PIXI.Container(),
            dark: new PIXI.Container(),
            hud: new PIXI.Container(),
            top: new PIXI.Container(),
            inputDisabled: new PIXI.Container() };
        //
        this._root.addChild( this._layers.bg );
        this._root.addChild( this._layers.intro );
        this._root.addChild( this._layers.world );
        this._root.addChild( this._layers.vfx );
        this._root.addChild( this._layers.dark );
        this._root.addChild( this._layers.hud );
        this._root.addChild( this._layers.top );
        this._root.addChild( this._layers.inputDisabled );

        const kBgIndex: number = ServiceLocator.saved.data.selBoardIndex; // TEST:
        this._bg = new PIXI.Sprite( ServiceLocator.resources[ "gp_bg_" + kBgIndex.toString() ].texture );
        this._bg.anchor.set( 0.5 );
        this._bg.position.set( ServiceLocator.app.width * 0.5, ServiceLocator.app.height * 0.5 );
        this._layers.bg.addChild( this._bg );
        //
        this._bgMaskAnim = new SpriteMaskAnim( Utils.createTexArray( "gp_mask_bg_", 9 ) );
        this._bgMaskAnim.fps = 30;
        this._bgMaskAnim.init();
        this._bgMaskAnim.anchor.set( 0.5 );
        this._bgMaskAnim.position.copyFrom( this._bg.position );
        this._layers.bg.addChild( this._bgMaskAnim );
        this._bgMaskAnim.onCompleted.add( () => { 
            this._bg.mask = null;

            this._bgMaskAnim.parent.removeChild( this._bgMaskAnim );
            this._bgMaskAnim.destroy();
            this._bgMaskAnim = null;

            this._dartboardInner.visible = true;
            this._dartboardInner.mask = this._dartboardInnerMaskAnim;
            //
            this._dartboardInnerMaskAnim.visible = true;
        } );
        //
        this._bg.mask = this._bgMaskAnim;
        //
        this._arrSprayHowl[ 0 ] = ServiceLocator.audio.playSfx( "intro_spray_0", { loop: true } );

        this._dartboardInner = new PIXI.Sprite( ServiceLocator.resources[ "gp_inner_" + kBgIndex.toString() ].texture );
        this._dartboardInner.scale.set( 1.04 );
        this._dartboardInner.anchor.set( 0.5 );
        this._dartboardInner.position.set( ServiceLocator.app.width * 0.5, ServiceLocator.app.height * 0.5 - 110 );
        this._layers.intro.addChild( this._dartboardInner );
        //
        this._dartboardInnerMaskAnim = new SpriteMaskAnim( Utils.createTexArray( "gp_mask_inner_", 10 ) );
        this._dartboardInnerMaskAnim.fps = 30;
        this._dartboardInnerMaskAnim.init();
        this._dartboardInnerMaskAnim.scale.copyFrom( this._dartboardInner.scale );
        this._dartboardInnerMaskAnim.anchor.set( 0.5 );
        this._dartboardInnerMaskAnim.position.copyFrom( this._dartboardInner.position );
        this._layers.intro.addChild( this._dartboardInnerMaskAnim );
        //
        this._dartboardInner.visible = false;
        this._dartboardInnerMaskAnim.visible = false;
        this._dartboardInnerMaskAnim.onCompleted.add( () => { 
            this._dartboardInner.mask = null;

            this._dartboardInnerMaskAnim.parent.removeChild( this._dartboardInnerMaskAnim );
            this._dartboardInnerMaskAnim.destroy();
            this._dartboardInnerMaskAnim = null;

            this._dartboardFrame.visible = true;
            this._dartboardFrame.mask = this._dartboardFrameMaskAnim;
            //
            this._dartboardFrameMaskAnim.visible = true;
        } );

        this._dartboardFrame = new PIXI.Sprite( ServiceLocator.resources[ "gp_frame_" + kBgIndex.toString() ].texture );
        this._dartboardFrame.scale.copyFrom( this._dartboardInner.scale );
        this._dartboardFrame.anchor.set( 0.5 );
        this._dartboardFrame.position.copyFrom( this._dartboardInner.position );
        this._layers.intro.addChildAt( this._dartboardFrame, 0 );
        //
        this._dartboardFrameMaskAnim = new SpriteMaskAnim( Utils.createTexArray( "gp_mask_frame_", 6 ) );
        this._dartboardFrameMaskAnim.fps = 30;
        this._dartboardFrameMaskAnim.init();
        this._dartboardFrameMaskAnim.scale.copyFrom( this._dartboardInner.scale );
        // TEST:
        //this._dartboardFrameMaskAnim.scale.x *= 1.2;
        //this._dartboardFrameMaskAnim.scale.y *= 1.2;
        //
        this._dartboardFrameMaskAnim.anchor.set( 0.5 );
        this._dartboardFrameMaskAnim.position.copyFrom( this._dartboardInner.position );
        this._layers.intro.addChildAt( this._dartboardFrameMaskAnim, 0 );
        //
        this._dartboardFrame.visible = false;
        this._dartboardFrameMaskAnim.visible = false;
        this._dartboardFrameMaskAnim.onCompleted.add( () => { 
            this._dartboardFrame.mask = null;

            this._dartboardFrameMaskAnim.parent.removeChild( this._dartboardFrameMaskAnim );
            this._dartboardFrameMaskAnim.destroy();
            this._dartboardFrameMaskAnim = null;

            //this._dartboard.visible = true; // TEST: NO ZOMM:

            for ( let i: number = 0; i < 2; ++i )
            {
                this._arrUserBaseAnim[ i ].visible = true;
                this._arrUserBaseAnim[ i ].play();
            }

            this._arrSprayHowl[ 0 ].stop();
            this._arrSprayHowl[ 0 ] = null;
        } );

        for ( let i: number = 0; i < 2; ++i )
        {
            let userBaseAnim: PIXI.AnimatedSprite = new PIXI.AnimatedSprite( Utils.createTexArray( "hud_user_base_", 5 ) );
            userBaseAnim.autoUpdate = false;
            userBaseAnim.animationSpeed = 30;
            userBaseAnim.loop = false;
            this._layers.intro.addChild( userBaseAnim );
            userBaseAnim.visible = false;
            if ( i == 1 )
            {
                userBaseAnim.scale.x *= -1;

                userBaseAnim.onComplete = () => { 
                    this._roundBase.visible = Session.i.gameType != game_type.POINTS_MATCHMAKING
                        && Session.i.gameType != game_type.POINTS_PRIVATE
                        && Session.i.gameType != game_type.POINTS_OFFLINE;

                    for ( let j: number = 0; j < 2; ++j )
                    {
                        this._arrPlayer[ j ].visible = true;
                        
                        this._arrUserBaseAnim[ j ].parent.removeChild( this._arrUserBaseAnim[ j ] );
                        this._arrUserBaseAnim[ j ].destroy();
                        this._arrUserBaseAnim[ j ] = null;
                    }
                    this._arrUserBaseAnim = null;

                    this._nextPlayer( this._arrPlayer[ 0 ] );

                    gsap.to( this._settings, { alpha: 1, duration: 0.4, ease: "linear.inOut" } );
                    //
                    let tl: gsap.core.Timeline = new gsap.core.Timeline();
                    tl.to( this._dartMoveWrapper, { y: this._kDartMoveBaseY, duration: 0.4, ease: "back.out" } );
                    if ( this._arrPlayer[ 0 ] == this._myPlayer )
                    {
                        tl.call ( () => { this._inputState = input_state_type.IDLE; } );
                    }
                    ServiceLocator.game.tweenGroup.add( tl );
                    //
                    gsap.to( this._remainingDartsWrapper, { alpha: 1, duration: 0.4, ease: "sine.in" } );
                };
            }

            this._arrUserBaseAnim.push( userBaseAnim );
        }

        /*let darkBg: PIXI.Graphics = new PIXI.Graphics();
        darkBg.beginFill( 0x000000, 1 );
        darkBg.drawRect( -ServiceLocator.app.safeAreaMarginX, -ServiceLocator.app.safeAreaMarginY, ServiceLocator.app.safeAreaWidth, ServiceLocator.app.safeAreaHeight );
        darkBg.endFill();
        this._layers.dark.addChild( darkBg ); 
        this._layers.dark.alpha = 0;*/

        // Create players.
        let maxUsernameWidth: number = 0;
        for ( let i: number = 0; i < Session.i.room.length; ++i )
        {
            let tempUsername: PIXI.Text = new PIXI.Text( Session.i.room[ i ].username.prefix, {
                fontSize: 30, 
                fontFamily: "teko_regular", 
                fill: 0x192f62, 
                stroke: 0xffffff, 
                strokeThickness: 4,
                dropShadow: true,
                dropShadowAngle: 2,
                dropShadowColor: "#ffffff",
                dropShadowDistance: 2,
                lineJoin: "round"
            } );
            if ( tempUsername.width > maxUsernameWidth )
            {
                maxUsernameWidth = tempUsername.width;
            }
        }
        let myProfileIndex: number = null;
        let profileIndex: number = 0;
        for ( let profile of Session.i.room )
        {
            let player: Player = new Player();
            player.id = profileIndex;
            player.name = profile.username.prefix;
            player.maxUsernameWidth = maxUsernameWidth;
            player.portraitUrl = profile.avatar.src;
            player.score = this._gameRulesType == game_rules_type.CLASSIC ? 0 : 301;
            player.isBot = ( profile as any ).isBot != undefined;

            this._layers.hud.addChild( player );

            this._arrPlayer.push( player );

            if ( myProfileIndex === null && Session.i.room[ profileIndex ].uid == Session.i.profile.uid )
            {
                myProfileIndex = profileIndex;
            }

            profileIndex += 1;
        }
        //
        this._myPlayer = this._arrPlayer[ myProfileIndex ];
        this._myPlayer.side = player_side_type.LEFT;
        this._myPlayer.init();
        this._myPlayer.position.set( 60, 14 );
        //
        let opponentPlayer: Player = this._arrPlayer[ 1 - this._myPlayer.id ];
        opponentPlayer.side = player_side_type.RIGHT;
        opponentPlayer.init();
        opponentPlayer.position.set( ServiceLocator.app.width - 60, this._myPlayer.y );
        //
        this._arrPlayer[ 0 ].nextPlayer = this._arrPlayer[ 1 ];
        this._arrPlayer[ 1 ].nextPlayer = this._arrPlayer[ 0 ];
        //
        this._arrPlayer[ 0 ].visible = false;
        this._arrPlayer[ 1 ].visible = false;
        //
        this._arrUserBaseAnim[ 0 ].position.set(  this._arrPlayer[ 0 ].x - 54, this._arrPlayer[ 1 ].y - 6 );
        this._arrUserBaseAnim[ 1 ].position.set(  this._arrPlayer[ 1 ].x + 54, this._arrPlayer[ 1 ].y - 6 );

        this._remainingDartsWrapper = new PIXI.Container();
        this._remainingDartsWrapper.alpha = 0;
        this._remainingDartsWrapper.position.set( ServiceLocator.app.width - 20, ServiceLocator.app.height - 20 );
        this._layers.hud.addChild( this._remainingDartsWrapper );
        //
        for ( let i: number = 0; i < 3; ++i )
        {
            let dart: PIXI.Sprite = new PIXI.Sprite( ServiceLocator.resources.hud_dart_p1.texture );
            dart.anchor.set( 1 );
            dart.x = -i * ( dart.texture.width + 6 );
            this._remainingDartsWrapper.addChildAt( dart, 0 );
        }

        this._remainingDartsBase = new PIXI.Sprite( ServiceLocator.resources.hud_bottom_label.texture );
        this._remainingDartsBase.visible = false;
        this._remainingDartsBase.anchor.set( 0.5 );
        this._remainingDartsBase.position.set( ServiceLocator.app.width * 0.5, ServiceLocator.app.height - this._remainingDartsBase.texture.height * 0.5 - 20 );
        this._layers.hud.addChild( this._remainingDartsBase );
        //
        this._remainingDartsTextWrapper = new PIXI.Container();
        this._remainingDartsTextWrapper.y = 4;
        //
        let dartCountText: PIXI.Text = new PIXI.Text( "3 ", { fontFamily: "jaro_regular", fontSize: 36, fill: 0xd0f400 } );
        dartCountText.anchor.y = 0.5;
        this._remainingDartsTextWrapper.addChild( dartCountText );
        //
        let dartDescText: PIXI.Text = new PIXI.Text( "Left", { fontFamily: "jaro_regular", fontSize: 36, fill: 0xffffff } );
        dartDescText.anchor.y = 0.5;
        dartDescText.x = dartCountText.width;
        this._remainingDartsTextWrapper.addChild( dartDescText );
        //
        this._remainingDartsTextWrapper.position.set( -this._remainingDartsTextWrapper.width * 0.5, 0 );
        this._remainingDartsBase.addChild( this._remainingDartsTextWrapper );
        
        this._dartHitAnim = new PIXI.AnimatedSprite( Utils.createTexArray( "vfx_hit_", 13 ) );
        this._dartHitAnim.autoUpdate = false;
        this._dartHitAnim.animationSpeed = 50;
        this._dartHitAnim.loop = false;
        //this._dartHitAnim.scale.set( 0.8 );
        this._dartHitAnim.anchor.set( 0.5 );
        this._dartHitAnim.visible = false;
        this._layers.vfx.addChild( this._dartHitAnim );
        this._dartHitAnim.onComplete = () => { this._dartHitAnim.visible = false; }

        this._roundBase = new PIXI.Sprite( ServiceLocator.resources.hud_round_base.texture );
        this._roundBase.visible = false;
        this._roundBase.anchor.set( 0.5 );
        this._roundBase.position.set( ServiceLocator.app.width * 0.5, this._roundBase.texture.height * 0.5 + 16 );
        this._layers.hud.addChild( this._roundBase );
        //
        for ( let i: number = 0; i < 3; ++i )
        {
            let roundDot: PIXI.Sprite = new PIXI.Sprite( ServiceLocator.resources[ i == 0 ? "hud_round_dot_on" : "hud_round_dot_off" ].texture );
            roundDot.anchor.set( 0.5 );
            roundDot.x = -24 + i * 24;
            this._roundBase.addChild( roundDot );
        }

        this._yourTurnBase = new PIXI.Sprite( ServiceLocator.resources.hud_your_turn_label.texture );
        this._yourTurnBase.scale.x = 0;
        this._yourTurnBase.anchor.set( 0.5 );
        this._yourTurnBase.position.set( ServiceLocator.app.width * 0.5, ServiceLocator.app.height - this._yourTurnBase.height * 0.5 - 20 );
        this._layers.hud.addChild( this._yourTurnBase );
        //
        this._yourTurnText = new PIXI.Text( "YOUR TURN", { fontFamily: "jaro_regular", fill: 0xffffff, fontSize: 40 } );
        this._yourTurnText.anchor.set( 0.5 );
        this._yourTurnBase.addChild( this._yourTurnText );
        //
        if ( Session.i.gameType == game_type.CLASSIC_OFFLINE || Session.i.gameType == game_type.POINTS_OFFLINE )
        {
            this._yourTurnText.text = "P1's TURN";
        }

        this._bannerBase = new PIXI.Sprite( ServiceLocator.resources[ "hud_banner_" + Math.floor( Math.random() * 3 ) ].texture );
        this._bannerBase.visible = false;
        this._bannerBase.anchor.set( 0.5 );
        this._bannerBase.position.set( ServiceLocator.app.width * 0.5, ServiceLocator.app.height * 0.5 );
        this._layers.hud.addChild( this._bannerBase );
        //
        this._bannerMaskIn = new SpriteMaskAnim( Utils.createTexArray( "hud_mask_banner_in_", 7 ) );
        this._bannerMaskIn.fps = 30;
        this._bannerMaskIn.init();
        this._bannerMaskIn.visible = false;
        this._bannerMaskIn.anchor.set( 0.5 );
        this._bannerMaskIn.position.copyFrom( this._bannerBase );
        this._layers.hud.addChild( this._bannerMaskIn );
        //
        this._bannerMaskOut = new SpriteMaskAnim( Utils.createTexArray( "hud_mask_banner_out_", 7 ) );
        this._bannerMaskOut.fps = 30;
        this._bannerMaskOut.init();
        this._bannerMaskOut.visible = false;
        this._bannerMaskOut.anchor.set( 0.5 );
        this._bannerMaskOut.position.copyFrom( this._bannerBase );
        this._layers.hud.addChild( this._bannerMaskOut );

        this._scoreWrapper = new PIXI.Container();
        this._scoreWrapper.visible = false;
        this._layers.vfx.addChild( this._scoreWrapper );
        //
        this._scoreBase = new PIXI.Sprite( ServiceLocator.resources.hud_score_added_label.texture );
        this._scoreBase.anchor.set( 0.53 );
        this._scoreWrapper.addChild( this._scoreBase );
        //
        this._scoreText = new PIXI.Text( "", { 
            fontSize: 70, 
            fontFamily: "college_tm_regular", 
            fill: 0xffffff, 
            dropShadow: true,
            dropShadowAngle: 2,
            dropShadowColor: "#40ddbc",
            dropShadowDistance: 4 } );
        this._scoreText.x = -14;
        this._scoreText.anchor.set( 0.5 );
        this._scoreText.angle = -4;
        this._scoreWrapper.addChild( this._scoreText );
        //
        this._scoreMultBadge = new PIXI.Sprite();
        this._scoreMultBadge.anchor.set( 0.5 );
        this._scoreMultBadge.visible = false;
        this._scoreMultBadge.position.set( 56, -70 );
        this._scoreWrapper.addChild( this._scoreMultBadge );
        
        this._checkoutBustBanner = new PIXI.Sprite();
        this._checkoutBustBanner.visible = false;
        this._checkoutBustBanner.scale.y = 0;
        this._layers.vfx.addChild( this._checkoutBustBanner );
        
        this._bullsEyeBanner = new PIXI.Sprite();
        this._bullsEyeBanner.visible = false;
        this._bullsEyeBanner.scale.y = 0;
        this._layers.vfx.addChild( this._bullsEyeBanner );

        if ( ServiceLocator.saved.data.isFirstGame )
        {
            this._tutorialHand = new PIXI.Sprite( ServiceLocator.resources.hud_tutorial_hand.texture );
            this._tutorialHand.visible = false;
            this._tutorialHand.scale.set( 1.2 );
            this._tutorialHand.anchor.set( 0.19, 0.15 );
            this._tutorialHand.position.set( ServiceLocator.app.width * 0.5, ServiceLocator.app.height - 300 );
            this._layers.hud.addChild( this._tutorialHand );
        }

        this._settings = new Settings();
        this._settings.init();
        this._settings.alpha = 0;
        this._settings.position.set( this._arrPlayer[ 1 - myProfileIndex ].x, 120 );
        this._layers.hud.addChild( this._settings );
        this._settings.onTutorial.add( () => { 
            let tutorialPopup: TutorialPopup = new TutorialPopup();
            tutorialPopup.init();
            this._root.addChild( tutorialPopup );
            tutorialPopup.onClosed.add( ( popup: TutorialPopup ) => {
                popup.end();
            } );
        } );
        this._settings.onHome.add( () => {
            let leaveConfirmationPopup: LeaveConfirmationPopup = new LeaveConfirmationPopup();
            leaveConfirmationPopup.onEnter();
            leaveConfirmationPopup.onClosed.add( ( popup: LeaveConfirmationPopup, isAccept: boolean ) => {
                popup.onLeave();
                if ( isAccept )
                {
                    this._onCompleted.dispatch( screen_type.LOBBY );
                }
            } );
        } );

        this._create3dScene();
        //this._dartboard.visible = false; // TEST: NO ZOOM:
        this._dartMoveWrapper.y -= 4;

        // TEST:
        /*this._arrPlayer[ 0 ].visible = true;
        let testUserBase: PIXI.Sprite = new PIXI.Sprite( ServiceLocator.resources.hud_user_base_4.texture );
        testUserBase.alpha = 0.5;
        testUserBase.position.copyFrom( this._arrPlayer[ 0 ].position );
        testUserBase.y -= 6;
        testUserBase.x -= 54;
        this._arrPlayer[ 0 ].parent.addChild( testUserBase );*/

        /*this._dartboardEntryTl = new gsap.core.Timeline();
        this._dartboardNumbers.rotationQuaternion.setEulerAngles( 0, 0, 45 );
        this._dartboardNumbers.alpha = 0;
        this._innerDartboard.rotationQuaternion.setEulerAngles( 0, 0, 45 );
        this._innerDartboard.alpha = 0;
        this._innerDartboard.scale.set( 0 );
        //
        let dartboardNumbersAngle: any = { value: 45 };
        let innerDartboardAngle: any = { value: 45 };
        this._dartboardEntryTl.to( dartboardNumbersAngle, { value: -20, duration: 0.8, ease: "sine.out", onUpdate: () => { this._dartboardNumbers.rotationQuaternion.setEulerAngles( 0, 0, dartboardNumbersAngle.value ); } }, 0 );
        this._dartboardEntryTl.to( dartboardNumbersAngle, { value: 0, duration: 1, ease: "sine.out", onUpdate: () => { this._dartboardNumbers.rotationQuaternion.setEulerAngles( 0, 0, dartboardNumbersAngle.value ); } }, ">" );
        this._dartboardEntryTl.to( this._dartboardNumbers, { alpha: 1, duration: 0.4, ease: "linear.inOut" }, 0 );
        //
        const kInnerScaleDuration: number = 0.8;
        this._dartboardEntryTl.to( innerDartboardAngle, { value: 0, duration: kInnerScaleDuration, ease: "sine.out", onUpdate: () => { this._innerDartboard.rotationQuaternion.setEulerAngles( 0, 0, innerDartboardAngle.value ); } }, 0 );
        this._dartboardEntryTl.to( this._innerDartboard, { alpha: 1, duration: 0.2, ease: "linear.inOut" }, 0 );
        this._dartboardEntryTl.to( this._innerDartboard.scale, { x: this._kDartboardScale, y: this._kDartboardScale, duration: kInnerScaleDuration, ease: "sine.out" }, 0 );
        this._dartboardEntryTl.call( () => { 
            this._innerDartboardOverlay.visible = true; 
            this._innerDartboardOverlay.scale.set( 38 * 2 * this._dartboard.scale.x / this._innerDartboardOverlay.texture.width );
        }, null, kInnerScaleDuration );
        this._dartboardEntryTl.call( () => { this._innerDartboardOverlay.scale.set( 75 * 2 * this._dartboard.scale.x / this._innerDartboardOverlay.texture.width ); }, null, kInnerScaleDuration + 0.1 );
        this._dartboardEntryTl.call( () => { this._innerDartboardOverlay.scale.set( 300 * 2 * this._dartboard.scale.x / this._innerDartboardOverlay.texture.width ); }, null, kInnerScaleDuration + 0.2 );
        this._dartboardEntryTl.call( () => { this._innerDartboardOverlay.scale.set( 342 * 2 * this._dartboard.scale.x / this._innerDartboardOverlay.texture.width ); }, null, kInnerScaleDuration + 0.3 );
        this._dartboardEntryTl.call( () => { this._innerDartboardOverlay.scale.set( 528 * 2 * this._dartboard.scale.x / this._innerDartboardOverlay.texture.width ); }, null, kInnerScaleDuration + 0.4 );
        this._dartboardEntryTl.call( () => { this._innerDartboardOverlay.scale.set( 576 * 2 * this._dartboard.scale.x / this._innerDartboardOverlay.texture.width ); }, null, kInnerScaleDuration + 0.5 );
        this._dartboardEntryTl.call( () => { this._innerDartboardOverlay.scale.set( 528 * 2 * this._dartboard.scale.x / this._innerDartboardOverlay.texture.width ); }, null, kInnerScaleDuration + 0.6 );
        this._dartboardEntryTl.call( () => { this._innerDartboardOverlay.scale.set( 342 * 2 * this._dartboard.scale.x / this._innerDartboardOverlay.texture.width ); }, null, kInnerScaleDuration + 0.7 );
        this._dartboardEntryTl.call( () => { this._innerDartboardOverlay.scale.set( 300 * 2 * this._dartboard.scale.x / this._innerDartboardOverlay.texture.width ); }, null, kInnerScaleDuration + 0.8 );
        this._dartboardEntryTl.call( () => { this._innerDartboardOverlay.scale.set( 75 * 2 * this._dartboard.scale.x / this._innerDartboardOverlay.texture.width ); }, null, kInnerScaleDuration + 0.9 );
        this._dartboardEntryTl.call( () => { this._innerDartboardOverlay.scale.set( 38 * 2 * this._dartboard.scale.x / this._innerDartboardOverlay.texture.width ); }, null, kInnerScaleDuration + 1 );
        this._dartboardEntryTl.call( () => { 
            this._innerDartboardOverlay.visible = false; 

            if ( this._roundBase.visible )
            {
                this._showRoundBannerNotification( "hud_round_one" );
            }

            this._nextPlayer( this._arrPlayer[ 0 ] );
        }, null, 2.1 );
        //
        ServiceLocator.game.tweenGroup.add( this._dartboardEntryTl );*/        

        this._inputState = input_state_type.DISABLED;

        if ( !Session.i.profile.isFake && !ServiceLocator.saved.data.isFirstGame )
        {
            ServiceLocator.multiplayerEventManager.onEventsReceived.add( this._onMultiplayer_EventsReceived, this );
            ServiceLocator.multiplayerEventManager.onPlayerPresenceUpdated.add( this._onMultiplayer_PlayerPresenceUpdated, this );
        }
        ServiceLocator.multiplayerEventManager.onProfileChange.add( this._onProfile_Changed, this );

        this._root.interactive = true;
        this._root.on( "pointerdown", this._onDart_Grabbed.bind( this ) );
        this._root.on( "pointerup", this._onDart_Released.bind( this ) );
        this._root.on( "pointerupoutside", this._onDart_Released.bind( this ) );
    }

        private _create3dScene(): void
        {
            /*( ServiceLocator.app.renderer as any ).plugins.pipeline._spriteRenderer.state.depthTest = false;
            ( ServiceLocator.app.renderer as any ).plugins.pipeline._spriteRenderer.state.depthMask = false;

            console.log( PIXI3D.Camera.main.x + ":: " + PIXI3D.Camera.main.y + " :: " + PIXI3D.Camera.main.z );*/

            //PIXI3D.Camera.main.orthographic = true; // TEST:

            const kMarginZ: number = this._kPixelToWorldUnits;
            //this._dartboard = new PIXI3D.Sprite3D( ServiceLocator.resources.gp_board_01_empty.texture );
            // TEST: NO ZOOM:
            /*this._dartboard = new PIXI3D.Sprite3D( ServiceLocator.resources[ "gp_inner_" + bgIndex.toString() ].texture );
            this._dartboard.anchor.set( 0.5 );
            this._dartboard.scale.set( this._kDartboardScale * 3 );
            this._dartboard.position.set( 0, this._kDartboardBaseY, -this._kDarboardDistanceZ );
            this._layers.world.addChild( this._dartboard );*/
            //
            //
            /*this._innerDartboard = new PIXI3D.Sprite3D( ServiceLocator.resources.gp_inner_board_01_empty.texture );
            this._innerDartboard.anchor.set( 0.5 );
            this._innerDartboard.scale.set( this._kDartboardScale );
            this._innerDartboard.position.set( 0, this._dartboard.y, this._dartboard.z + kMarginZ );
            this._layers.world.addChild( this._innerDartboard );    
            //
            this._dartboardNumbers = new PIXI3D.Sprite3D( ServiceLocator.resources.gp_board_numbers.texture );
            this._dartboardNumbers.anchor.set( 0.5 );
            this._dartboardNumbers.scale.set( this._kDartboardScale );
            this._dartboardNumbers.position.set( 0, this._dartboard.y, this._innerDartboard.z + kMarginZ );
            this._layers.world.addChild( this._dartboardNumbers );    
            //
            this._innerDartboardOverlay = new PIXI3D.Sprite3D( ServiceLocator.resources.gp_highlight_0.texture );
            this._innerDartboardOverlay.anchor.set( 0.5 );
            this._innerDartboardOverlay.alpha = 0.25;
            this._innerDartboardOverlay.visible = false;
            this._innerDartboardOverlay.blendMode = PIXI.BLEND_MODES.ADD;
            this._innerDartboardOverlay.position.set( 0, this._dartboard.y, this._dartboardNumbers.z + kMarginZ );
            this._layers.world.addChild( this._innerDartboardOverlay );   */

            this._targetSectorOutlineWrapper = new PIXI3D.Container3D();
            this._targetSectorOutlineWrapper.visible = false;
            this._targetSectorOutlineWrapper.position.set( 0, this._kDartboardBaseY, -this._kDarboardDistanceZ + kMarginZ );
            this._layers.world.addChild( this._targetSectorOutlineWrapper );   

            this._sectorHighlightWrapper = new PIXI3D.Container3D();
            this._sectorHighlightWrapper.visible = false;
            //this._sectorHighlightWrapper.position.set( 0, this._dartboard.y, this._dartboardNumbers.z + kMarginZ );
            this._sectorHighlightWrapper.position.set( 0, this._kDartboardBaseY, -this._kDarboardDistanceZ + kMarginZ );
            this._sectorHighlightWrapper.rotationQuaternion.setEulerAngles( 0, 0, 90 );
            this._layers.world.addChild( this._sectorHighlightWrapper );   
            //
            this._sectorHighlightOverlay = new PIXI3D.Sprite3D( ServiceLocator.resources.gp_highlight_2.texture );
            this._sectorHighlightOverlay.scale.set( this._kDartboardScale );
            this._sectorHighlightOverlay.anchor.set( 0.5, 1 );
            this._sectorHighlightWrapper.addChild( this._sectorHighlightOverlay );    
            //
            this._sectorHighlightOutline = new PIXI3D.Sprite3D( ServiceLocator.resources.gp_outline_2.texture );
            this._sectorHighlightOutline.scale.set( this._kDartboardScale );
            this._sectorHighlightOutline.anchor.set( 0.5, 1 );
            this._sectorHighlightOutline.z = kMarginZ;
            this._sectorHighlightWrapper.addChild( this._sectorHighlightOutline );   

            // TEST:
            /*let sectorHighlightOverlay: PIXI3D.Sprite3D = new PIXI3D.Sprite3D( ServiceLocator.resources.gp_highlight_2.texture );
            sectorHighlightOverlay.scale.set( this._kDartboardScale );
            sectorHighlightOverlay.anchor.set( 0.5, 1 );
            this._sectorHighlightWrapper.addChild( sectorHighlightOverlay );    
            //
            let sectorHighlightOutline: PIXI3D.Sprite3D = new PIXI3D.Sprite3D( ServiceLocator.resources.gp_outline_2.texture );
            sectorHighlightOutline.scale.set( this._kDartboardScale );
            sectorHighlightOutline.anchor.set( 0.5, 1 );
            sectorHighlightOutline.z = kMarginZ;
            this._sectorHighlightWrapper.addChild( sectorHighlightOutline );  */
            //
            
            this._dartMoveWrapper = new PIXI3D.Container3D();
            this._dartMoveWrapper.y = this._kDartMoveBaseY;
            this._dartMoveWrapper.z = this._kDartMoveBaseZ;
            this._layers.world.addChild( this._dartMoveWrapper );

            this._dartRotYWrapper = new PIXI3D.Container3D();
            this._dartMoveWrapper.addChild( this._dartRotYWrapper );

            this._dartBaseTransformWrapper = new PIXI3D.Container3D();
            this._dartBaseTransformWrapper.z = this._kDartBaseZ;
            this._dartBaseTransformWrapper.y = this._kDartBaseY;
            this._dartBaseTransformWrapper.scale.set( 3 );
            this._dartBaseTransformWrapper.rotationQuaternion.setEulerAngles( 45, 0, 0 );
            this._dartRotYWrapper.addChild( this._dartBaseTransformWrapper );

            this._dartRotZWrapper = new PIXI3D.Container3D();
            this._dartBaseTransformWrapper.addChild( this._dartRotZWrapper );    
            
            /*this._dart = this._dartRotZWrapper.addChild( PIXI3D.Model.from( ( ServiceLocator.resources.dart_green as any ).gltf ) );
            this._dart.meshes.forEach( ( mesh ) => {
                const mat = mesh.material as PIXI3D.StandardMaterial;
                mat.baseColor = new PIXI3D.Color( 1, 1, 1 ); // Diffuse Color
                mat.metallic = 0;                   // Reflectivity
                mat.roughness = 10;                  // Surface smoothness
            } );*/

            this._dart = this._dartRotZWrapper.addChild( PIXI3D.Model.from( ( ServiceLocator.resources[ "dart_" + Session.i.room[ this._arrPlayer[ 0 ].id ].dartSkinIndex.toString() ] as any ).gltf ) );
            //this._dartRotZWrapper.rotationQuaternion.setEulerAngles( 0, 0, 270 );
            this._dart.meshes.forEach( ( mesh ) => {
                const mat = mesh.material as PIXI3D.StandardMaterial;
                if ( this._arrPlayer[ 0 ] != this._myPlayer 
                    && Session.i.room[ 0 ].dartSkinIndex == Session.i.room[ 1 ].dartSkinIndex )
                {
                    mat.baseColorTexture = ServiceLocator.resources[ "alt_dart_texture_" + Session.i.room[ 0 ].dartSkinIndex.toString() ].texture;
                    mat.baseColorTexture.uvSet = 1;
                }
                mat.baseColor = new PIXI3D.Color( 1, 1, 1 ); // Diffuse Color
                mat.metallic = 0; // Reflectivity
                mat.roughness = 10; // Surface smoothness
            } );

            // Adds a directional light to the main lighting environment.
            let dirLight: PIXI3D.Light = new PIXI3D.Light();
            dirLight.type = PIXI3D.LightType.directional;
            dirLight.color = PIXI3D.Color.fromHex( 0xffffff );
            dirLight.transform.rotationQuaternion.setEulerAngles( 180, 0, 0 );
            dirLight.intensity = 5;
            PIXI3D.LightingEnvironment.main.lights.push( dirLight );

            // Adds a point light to the main lighting environment.
            /*let pointLight = new PIXI3D.Light()
            pointLight.type = PIXI3D.LightType.point;
            pointLight.color = PIXI3D.Color.fromHex( 0xffffff );
            pointLight.x = 0
            pointLight.y = -1
            pointLight.z = 0;
            pointLight.range = 500
            pointLight.intensity = 70
            PIXI3D.LightingEnvironment.main.lights.push(pointLight);*/

            //let control = new PIXI3D.CameraOrbitControl(ServiceLocator.app.renderer.view);

            // Creates a shadow casting light for the directional light.
            /*let shadowCastingLight = new PIXI3D.ShadowCastingLight(
                ServiceLocator.app.renderer, dirLight, {
                    shadowTextureSize: 512,
                    quality: PIXI3D.ShadowQuality.medium
                } );
            shadowCastingLight.softness = 1;
            shadowCastingLight.shadowArea = 8;

            // Enables shadows to be both casted and received for the model and ground.
            ServiceLocator.app.renderer.plugins.pipeline.enableShadows( this._dart, shadowCastingLight );*/
        }

        private _nextPlayer( player: Player = null ): void
        {
            // TEST:
            //this._showTargetSectorOutline( 12 );

            this._playerDartCount = 0;
            this._playerInactivityTimer = 0;
            Session.i.inactivityElapsedTime = 0;
            this._imAliveTimer = 0;

            // Remove scored darts.
            while ( this._arrScoredDartWrapper.length > 0 )
            {
                let dartWrapper: PIXI3D.Container3D = this._arrScoredDartWrapper.pop();
                this._layers.world.removeChild( dartWrapper );
                dartWrapper.destroy( { children: true } );
            }
            //
            while ( this._arrDartShadow.length > 0 )
            {
                let dartShadow: PIXI3D.Sprite3D = this._arrDartShadow.pop();
                this._layers.world.removeChild( dartShadow );
                dartShadow.destroy( { children: true } );
            }

            // Disable last player.
            if ( this._curPlayer )
            {
                if ( this._curPlayer == this._myPlayer )
                {
                    this._inputState = input_state_type.DISABLED;
                }
                this._curPlayer.setHighlighted( false );
            }

            // Choose next player.
            const kPrevCurPlayer: Player = this._curPlayer;
            if ( !player )
            {
                this._curPlayer = this._curPlayer.nextPlayer;
            }
            else
            {
                this._curPlayer = player;
            }

            this._playerRoundScore = this._curPlayer.score;
            
            if ( Session.i.gameType == game_type.CLASSIC_OFFLINE
                || Session.i.gameType == game_type.POINTS_OFFLINE )
            {
                Session.i.profile = Session.i.room[ this._curPlayer.id ];
                this._myPlayer = this._curPlayer;

                if ( this._yourTurnBase.scale.x > 0 )
                {
                    let tl: gsap.core.Timeline = new gsap.core.Timeline();
                    tl.to( this._yourTurnBase.scale, { y: 0, duration: 0.24, ease: "back.in" } );
                    tl.call( () => {
                        this._yourTurnText.text = this._myPlayer == this._arrPlayer[ 0 ] ? "P1's TURN" : "P2's TURN";
                    }, null, );
                    tl.to( this._yourTurnBase.scale, { y: 1, duration: 0.24, ease: "back.out" } );
                    ServiceLocator.game.tweenGroup.add( tl );
                }
            }
            
            if ( this._curPlayer == this._myPlayer )
            {
                ServiceLocator.game.tweenGroup.add( 
                    gsap.to( this._yourTurnBase.scale, { x: 1, duration: 0.24, ease: "back.out", delay: 0.4 } ) );
            }
            else
            {
                ServiceLocator.game.tweenGroup.add( 
                    gsap.to( this._yourTurnBase.scale, { x: 0, duration: 0.24, ease: "back.in" } ) );
            }

            let dartIconSuffix: string = null;
            if ( Session.i.gameType == game_type.CLASSIC_OFFLINE || Session.i.gameType == game_type.POINTS_OFFLINE )
            {
                dartIconSuffix = this._curPlayer.id == 0 ? "p1" : "p2";
            }
            else
            {
                dartIconSuffix = this._curPlayer == this._myPlayer ? "p1" : "p2";
            }
            for ( let i: number = 0; i < this._remainingDartsWrapper.children.length; ++i )
            {
                ( this._remainingDartsWrapper.children[ i ] as PIXI.Sprite ).texture = ServiceLocator.resources[ "hud_dart_" + dartIconSuffix ].texture;
            }

            ( this._remainingDartsTextWrapper.children[ 0 ] as PIXI.Text ).text = ( 3 - this._playerDartCount ).toString();

            
            if ( kPrevCurPlayer != this._curPlayer )
            {
                this._isNextAction = false;

                this._showPlayerTurnBanner( 
                    () => { 
                        this._managePlayerTurn( this._curPlayer ); 

                        this._isNextAction = true;
                    } );
            }
            else
            {
                this._isNextAction = true;

                this._managePlayerTurn( this._curPlayer );
            }
        }

            private _showPlayerTurnBanner( onComplete: () => void = null ): void
            {
                this._bannerBase.removeChildren();
                //
                if ( Session.i.gameType == game_type.CLASSIC_MATCHMAKING
                    || Session.i.gameType == game_type.CLASSIC_PRIVATE
                    || Session.i.gameType == game_type.CLASSIC_OFFLINE )
                {
                    let topText: PIXI.Text = new PIXI.Text( this._curPlayer == this._myPlayer ? "YOUR TURN" : "OPPONENT's TURN", { fontFamily: "jaro_regular", fill: 0x000000, fontSize: 50 } );
                    topText.anchor.set( 0.5, 1 );
                    this._bannerBase.addChild( topText );

                    if ( Session.i.gameType == game_type.CLASSIC_OFFLINE )
                    {
                        topText.text = this._myPlayer == this._arrPlayer[ 0 ] ? "P1's TURN" : "P2's TURN";
                    }
                    
                    let bottomText: PIXI.Text = new PIXI.Text( [ "First", "Second", "Last" ][ this._roundCount - 1 ] + " Round", { fontFamily: "jaro_regular", fill: /*[ 0x009444, 0xf7941d, 0xef4122 ][ this._roundCount - 1 ]*/0x009444, fontSize: 40 } );
                    bottomText.anchor.set( 0.5, 0 );
                    this._bannerBase.addChild( bottomText );

                    this._showBanner( onComplete, 1.6 );
                }
                else
                {
                    let messageText: PIXI.Text = new PIXI.Text( this._curPlayer == this._myPlayer ? "YOUR TURN" : "OPPONENT's TURN", { fontFamily: "jaro_regular", fill: 0x000000, fontSize: 50 } );
                    messageText.anchor.set( 0.5 );
                    this._bannerBase.addChild( messageText );

                    if ( Session.i.gameType == game_type.POINTS_OFFLINE )
                    {
                        messageText.text = this._myPlayer == this._arrPlayer[ 0 ] ? "P1's TURN" : "P2's TURN";
                    }

                    this._showBanner( onComplete, 1.4 );
                }
            }

                private _showBanner( onComplete: () => void, duration: number = 1 ): void
                {
                    //this._arrSprayHowl[ 0 ] = ServiceLocator.audio.playSfx( "intro_spray_1", { loop: true } );

                    this._bannerBase.texture = ServiceLocator.resources[ "hud_banner_" + Math.floor( Math.random() * 3 ) ].texture;
                    this._bannerBase.visible = true;
                    this._bannerBase.mask = this._bannerMaskIn;
                    
                    this._bannerMaskIn.reset();
                    this._bannerMaskIn.visible = true;
                    //
                    this._bannerMaskIn.onCompleted.addOnce( () => { 
                        this._bannerMaskIn.visible = false;
                        this._bannerBase.mask = null;

                        /*this._arrSprayHowl[ 0 ].stop();
                        this._arrSprayHowl[ 0 ] = null;*/

                        ServiceLocator.game.tweenGroup.add( 
                            gsap.delayedCall(
                                duration,
                                () => {
                                    this._bannerMaskOut.reset();
                                    this._bannerMaskOut.visible = true;
                                    this._bannerBase.mask = this._bannerMaskOut;

                                    //this._arrSprayHowl[ 1 ] = ServiceLocator.audio.playSfx( "intro_spray_2", { loop: true } );

                                    this._bannerMaskOut.onCompleted.addOnce( () => { 
                                        this._bannerMaskOut.visible = false;
                                        this._bannerBase.mask = null;
                                        this._bannerBase.visible = false;

                                        /*this._arrSprayHowl[ 1 ].stop();
                                        this._arrSprayHowl[ 1 ] = null;*/

                                        if ( onComplete )
                                        {
                                            onComplete();
                                        }
                                    }, this );
                                } ) );
                    }, this );
                }

                private _showRoundScoreBanner( player: Player, points: number, onComplete: () => void = null ): void
                {
                    this._bannerBase.removeChildren();
                    //
                    let messageWrapper: PIXI.Container = new PIXI.Container();
                    let playerName: string = null;
                    if ( Session.i.gameType == game_type.CLASSIC_OFFLINE
                        || Session.i.gameType == game_type.POINTS_OFFLINE )
                    {
                        playerName = this._myPlayer == this._arrPlayer[ 0 ] ? "P1" : "P2";
                    }
                    else
                    {
                        playerName = player == this._myPlayer ? "You" : player.name;
                    }
                    let playerText: PIXI.Text = new PIXI.Text( playerName, { fontFamily: "jaro_regular", fill: 0x009444, fontSize: 50 } );
                    playerText.anchor.set( 0, 0.5 );
                    messageWrapper.addChild( playerText );
                    let scoredText: PIXI.Text = new PIXI.Text( " scored ", { fontFamily: "jaro_regular", fill: 0x000000, fontSize: 50 } );
                    scoredText.anchor.set( 0, 0.5 );
                    scoredText.x = playerText.width;
                    messageWrapper.addChild( scoredText );
                    let pointsValueText: PIXI.Text = new PIXI.Text( points.toString(), { fontFamily: "jaro_regular", fill: 0x009444, fontSize: 50 } );
                    pointsValueText.anchor.set( 0, 0.5 );
                    pointsValueText.x = scoredText.x + scoredText.width;
                    messageWrapper.addChild( pointsValueText );
                    let pointsText: PIXI.Text = new PIXI.Text( " points", { fontFamily: "jaro_regular", fill: 0x000000, fontSize: 50 } );
                    pointsText.anchor.set( 0, 0.5 );
                    pointsText.x = pointsValueText.x + pointsValueText.width;
                    messageWrapper.addChild( pointsText );
                    messageWrapper.x = -messageWrapper.width * 0.5;
                    this._bannerBase.addChild( messageWrapper );

                    this._showBanner( onComplete, 1.6 );
            }

            private _managePlayerTurn( player: Player ): void
            {
                if ( player.isBot )
                {
                    ServiceLocator.game.tweenGroup.add(
                        gsap.delayedCall( 1 + Math.random() * 3, () => { 
                            this._isNextAction = false;
                            
                            this._autoThrowDart( player ); 
                        } ) );
                }
                /*else 
                {
                    if ( player == this._myPlayer )
                    {
                        this._inputState = input_state_type.IDLE;
                    }
                }*/
                
                player.setHighlighted( true );
            }

                private _autoThrowDart( player: Player ): void
                {
                    const kDirX: number = Session.i.prng.randInterval( 0, 1 ) == 1 ? 1 : -1;
                    const kDirY: number = Session.i.prng.randInterval( 0, 1 ) == 1 ? 1 : -1;

                    const kThrowRadius: number = ServiceLocator.saved.data.isFirstGame ? 6 : 4;
                    let target: PIXI3D.Point3D = new PIXI3D.Point3D( 
                            kDirX * Session.i.prng.randInterval( 0, 100 ) / 100 * kThrowRadius, 
                            this._kDartboardBaseY + kDirY * Session.i.prng.randInterval( 0, 100 ) / 100 * kThrowRadius, 
                            -this._kDarboardDistanceZ );
                    if ( ServiceLocator.saved.data.isFirstGame )
                    {
                        let dartScore: IDartScore = this._findDartScore( target );
                        while ( dartScore.area == "BULL"
                            || dartScore.area == "OUTER_BULL"
                            || dartScore.area == "TRIPLE"
                            || dartScore.area == "DOUBLE"
                            || ( dartScore.sector !== null && [6, 13, 4, 18, 1, 20, 5, 12, 9, 14, 11, 8, 16, 7, 19, 3, 17, 2, 15, 10][ dartScore.sector ] > 12 ) )
                        {
                            target = new PIXI3D.Point3D( 
                                kDirX * Session.i.prng.randInterval( 0, 100 ) / 100 * kThrowRadius, 
                                this._kDartboardBaseY + kDirY * Session.i.prng.randInterval( 0, 100 ) / 100 * kThrowRadius, 
                                -this._kDarboardDistanceZ )

                            dartScore = this._findDartScore( target );
                        }
                    }

                    this._throwDart( target, this._curPlayer );
                }

                    private _throwDart( target: PIXI3D.Point3D, player: Player ): void
                    {
                        console.log( "(x=" + target.x + ", y=" + target.y + ", z=" + target.z + ")" );

                        // TEST:
                        /*let plane = new PIXI3D.Sprite3D( ServiceLocator.resources.test0.texture );
                        plane.position.copyFrom( target );
                        plane.anchor.set( 0.5 );
                        this._layers.world.addChildAt( plane, 1 );*/

                        this._playerDartCount += 1;

                        for ( let targetSectorOutline of this._targetSectorOutlineWrapper.children )
                        {
                            targetSectorOutline.destroy( { children: true } );
                        }
                        this._targetSectorOutlineWrapper.removeChildren();
                        this._targetSectorOutlineWrapper.visible = false;

                        ( this._remainingDartsWrapper.children[ this._playerDartCount - 1 ] as PIXI.Sprite ).texture = null;

                        ( this._remainingDartsTextWrapper.children[ 0 ] as PIXI.Text ).text = ( 3 - this._playerDartCount ).toString();

                        if ( this._throwDartTl )
                        {
                            this._throwDartTl.kill();
                        }
                        this._throwDartTl = new gsap.core.Timeline();
                        ServiceLocator.game.tweenGroup.add( this._throwDartTl );
                        //
                        this._throwDartTl.to( this._dartMoveWrapper, { x: target.x, y: target.y - this._dartBaseTransformWrapper.y, z: target.z + ( 1.2 - this._kDartBaseZ ), duration: 0.3, ease: "linear.inOut" }, 0 );
                        this._throwDartTl.to( this._dartBaseTransformWrapper, { y: "+=2", duration: 0.15, yoyo: true, repeat: 1, ease: "sine.inOut" }, 0 );
                        this._throwDartTl.eventCallback( "onComplete", () => { 
                            const kScreenHit: PIXI.Point = PIXI3D.Camera.main.worldToScreen( target.x, target.y, target.z );
                            const kRootHit: PIXI.Point = this._root.toLocal( kScreenHit );
                            
                            this._dartHitAnim.visible = true;
                            this._dartHitAnim.position.copyFrom( kRootHit );
                            this._dartHitAnim.gotoAndPlay( 0 );

                            if ( !player.isBot && player == this._myPlayer && !Session.i.profile.isFake && !ServiceLocator.saved.data.isFirstGame )
                            {
                                window.GameSnacks.liveMultiplayer.sendEvent( { payload: JSON.stringify( { type: game_event_type.DART_THROWN, playerId: this._curPlayer.id, target: [ target.x, target.y, target.z ] } ) } );
                            }

                            const kDartScore: IDartScore = this._findDartScore( target );
                            if ( kDartScore.area != "MISS" )
                            {
                                this._highlightDartScore( kDartScore );
                            }
                            if ( this._gameRulesType == game_rules_type.CLASSIC )
                            {
                                player.addScore( kDartScore.score );

                                if ( kDartScore.area == "BULL" )
                                {
                                    this._showBullsEyeBanner( kRootHit );
                                }
                            }
                            else
                            {
                                if ( kDartScore.score <= this._curPlayer.score )
                                {
                                    player.addScore( -kDartScore.score );

                                    if ( player.score == 0 )
                                    {
                                        this._showCheckoutBanner( kRootHit );
                                    }
                                    else if ( kDartScore.area == "BULL" )
                                    {
                                        this._showBullsEyeBanner( kRootHit );
                                    }
                                }
                                else
                                {
                                    player.isBusted = true;

                                    this._showBustBanner( kRootHit );
                                }
                            }
                            //
                            this._showScoreAt( kDartScore, kRootHit );

                            /*if ( kDartScore.area == "DOUBLE" || kDartScore.area == "TRIPLE" )
                            {
                                this._showBannerNotification( kDartScore.area == "DOUBLE" ? "hud_double_score" : "hud_triple_score", "hud_score_multiplier_label" );
                            }*/

                            let dartShadow: PIXI3D.Sprite3D = new PIXI3D.Sprite3D( ServiceLocator.resources.dart_shadow.texture );
                            //dartShadow.anchor.set( 0.5, 0.2 );
                            //dartShadow.rotationQuaternion.setEulerAngles( 0, 0, -60 );
                            dartShadow.scale.set( 0.8 );
                            dartShadow.position.set( target.x - 57 * dartShadow.scale.x * this._kPixelToWorldUnits, target.y - 99 * dartShadow.scale.y * this._kPixelToWorldUnits, this._sectorHighlightWrapper.z + this._kPixelToWorldUnits * ( 2 + this._arrDartShadow.length ) );
                            this._layers.world.addChild( dartShadow );  
                            //
                            this._arrDartShadow.push( dartShadow );  

                            this._scoreTl.call( () => { this._onDartScored( player ); } );
                            /*ServiceLocator.game.tweenGroup.add( 
                                gsap.delayedCall( kDartScore.area == "DOUBLE" || kDartScore.area == "TRIPLE"  ? this._scoreTl.totalDuration() - 0.6 : 1.4, () => { 
                                    // TEST:
                                    /*PIXI3D.Camera.main.position.copyFrom( this._dartboard.position );
                                    PIXI3D.Camera.main.rotationQuaternion.setEulerAngles( 0, 90, 0 );
                                    PIXI3D.Camera.main.x -= 10;*/
                                    
                                    /*this._onDartScored( player ); 
                                } ) );*/

                            ServiceLocator.audio.playSfx( "dart_impact_" + ( Math.floor( Math.random() * 6 ) ).toString() );
                        }, null );
                        const kDartEndRotationZ: number = this._kDartEndRotationZ - 90 * Math.random();
                        this._throwDartTl.eventCallback( "onUpdate", () => {
                            this._dartBaseTransformWrapper.rotationQuaternion.setEulerAngles( 45 * ( 1 - this._throwDartTl.progress() ), 0, 0 );
                            this._dartRotZWrapper.rotationQuaternion.setEulerAngles( 0, 0, kDartEndRotationZ * this._throwDartTl.progress() );
                        }, null );

                        ServiceLocator.audio.playSfx( "dart_throw_" + ( Math.floor( Math.random() * 6 ).toString() ) );

                        // TEST:
                        /*let isFollowDart: boolean = kDartScore.area == "BULL" || kDartScore.area == "OUTER_BULL"
                            || kDartScore.area == "DOUBLE" || kDartScore.area == "TRIPLE";
                        if ( isFollowDart )
                        {
                            this._followDartTargetZ = target.z + 5;

                            if ( this._followDartTl )
                            {
                                this._followDartTl.kill();
                            }
                            this._followDartTl = new gsap.core.Timeline();
                            ServiceLocator.game.tweenGroup.add( this._followDartTl );
                            //
                            this._followDartTl.to( PIXI3D.Camera.main, { x: target.x, y: target.y, z: this._followDartTargetZ, duration: 1, ease: "sine.out" } );
                            this._followDartTl.call( () => {
                                PIXI3D.Camera.main.position.set( 0, 0, this._kCameraBaseZ );
                            }, null, ">+=1" );
                        }*/
                    }

                        private _showCheckoutBanner( dartPos: PIXI.Point ): void
                        {
                            this._checkoutBustBanner.texture = ServiceLocator.resources.hud_checkout.texture;
                            this._setCheckoutBustBannerPosition( this._checkoutBustBanner, dartPos );
                            
                            this._checkoutBustBanner.scale.y = 0;
                            this._checkoutBustBanner.visible = true;
                            //
                            let tl: gsap.core.Timeline = new gsap.core.Timeline();
                            tl.to( this._checkoutBustBanner.scale, { y: 1, duration: 0.26, ease: "back.out" }, 0.26 );
                            tl.to( this._checkoutBustBanner.scale, { y: 0, duration: 0.26, ease: "back.in" }, 1.26 );
                            tl.call( () => { this._checkoutBustBanner.visible = false; } );
                            ServiceLocator.game.tweenGroup.add( tl );

                            ServiceLocator.audio.playSfx( "checkout" );
                        }

                            private _setCheckoutBustBannerPosition( banner: PIXI.Sprite, dartPos: PIXI.Point ): void
                            {
                                if ( dartPos.x - 20 - banner.width > 0 )
                                {
                                    banner.anchor.set( 1, 0.5 );
                                    banner.position.set( dartPos.x - 20, dartPos.y ); 
                                }
                                else if ( dartPos.x + 20 + banner.width < ServiceLocator.app.width )
                                {
                                    banner.anchor.set( 0, 0.5 );
                                    banner.position.set( dartPos.x + 20, dartPos.y ); 
                                }
                                else
                                {
                                    banner.anchor.set( 0.5 );
                                    banner.position.set( dartPos.x, dartPos.y + 20 + banner.height * 0.5 ); 
                                }
                            }

                        private _showBustBanner( dartPos: PIXI.Point ): void
                        {
                            this._checkoutBustBanner.texture = ServiceLocator.resources.hud_bust.texture;
                            this._setCheckoutBustBannerPosition( this._checkoutBustBanner, dartPos );
                        
                            this._checkoutBustBanner.scale.y = 0;
                            this._checkoutBustBanner.visible = true;
                            //
                            let tl: gsap.core.Timeline = new gsap.core.Timeline();
                            tl.to( this._checkoutBustBanner.scale, { y: 1, duration: 0.26, ease: "back.out" }, 0.26 );
                            tl.to( this._checkoutBustBanner.scale, { y: 0, duration: 0.26, ease: "back.in" }, 1.26 );
                            tl.call( () => { this._checkoutBustBanner.visible = false; } );
                            ServiceLocator.game.tweenGroup.add( tl );

                            ServiceLocator.audio.playSfx( "bust" );
                        }

                        private _showBullsEyeBanner( dartPos: PIXI.Point ): void
                        {
                            ServiceLocator.audio.playSfx( "bullseye" );

                            if ( this._confettiAnim )
                            {
                                this._confettiAnim.end();
                            }
                            this._confettiAnim = new ConfettiAnim();
                            this._confettiAnim.duration = 1;
                            this._confettiAnim.confettiCountMult = 2;
                            this._confettiAnim.confettiCreateMult = 0.5;
                            this._confettiAnim.speedYMult = 2;
                            this._confettiAnim.frontContainer = this._layers.vfx;
                            this._confettiAnim.init();
                            this._layers.bg.addChild( this._confettiAnim );
                            this._confettiAnim.onCompleted.addOnce( () => { 
                                this._confettiAnim.end();
                                this._layers.bg.removeChild( this._confettiAnim );
                                this._confettiAnim.destroy( { children: true } );
                                this._confettiAnim = null;
                            } );

                            this._bullsEyeBanner.texture = ServiceLocator.resources.hud_bullseye.texture;
                            this._setCheckoutBustBannerPosition( this._bullsEyeBanner, dartPos );
                        
                            this._bullsEyeBanner.scale.y = 0;
                            this._bullsEyeBanner.visible = true;
                            //
                            let tl: gsap.core.Timeline = new gsap.core.Timeline();
                            tl.to( this._bullsEyeBanner.scale, { y: 1, duration: 0.26, ease: "back.out" }, 0.26 );
                            tl.to( this._bullsEyeBanner.scale, { y: 0, duration: 0.26, ease: "back.in" }, 1.26 );
                            tl.call( () => { this._bullsEyeBanner.visible = false; } );
                            ServiceLocator.game.tweenGroup.add( tl );
                        }

                        private _highlightDartScore( dartScore: IDartScore ): void
                        {
                            const kAngleZ: number = dartScore.sector === undefined ? 0 : -90 + dartScore.sector * 18;
                            this._sectorHighlightWrapper.rotationQuaternion.setEulerAngles( 0, 0, kAngleZ );
                            this._sectorHighlightWrapper.visible = true;
                            //
                            let overlayTextureIndex: number = null;
                            switch ( dartScore.area )
                            {
                                case "BULL": { overlayTextureIndex = 0; break; }
                                case "OUTER_BULL": { overlayTextureIndex = 1; break; }
                                case "INNER_SINGLE": { overlayTextureIndex = 2; break; }
                                case "TRIPLE": { overlayTextureIndex = 3; break; }
                                case "OUTER_SINGLE": { overlayTextureIndex = 4; break; }
                                case "DOUBLE": { overlayTextureIndex = 5; break; }
                            }
                            if ( dartScore.sector === undefined )
                            {
                                this._sectorHighlightOverlay.anchor.set( 0.5 );
                            }
                            else
                            {
                                this._sectorHighlightOverlay.anchor.set( 0.5, 1 );
                            }
                            this._sectorHighlightOverlay.texture = ServiceLocator.resources[ "gp_highlight_" + overlayTextureIndex.toString() ].texture;
                            this._sectorHighlightOverlay.alpha = 0;
                            this._sectorHighlightOverlay.y = [ 0, 0, 75, 297, 338, 528 - 2 ][ overlayTextureIndex ] * this._sectorHighlightOverlay.scale.x * this._kPixelToWorldUnits;
                            //
                            this._sectorHighlightOutline.texture = ServiceLocator.resources[ "gp_outline_" + overlayTextureIndex.toString() ].texture;
                            this._sectorHighlightOutline.alpha = 1;
                            this._sectorHighlightOutline.y = this._sectorHighlightOverlay.y;
                            if ( dartScore.sector === undefined )
                            {
                                this._sectorHighlightOutline.anchor.set( 0.5 );
                            }
                            else
                            {
                                this._sectorHighlightOutline.anchor.set( 0.5, 1 );
                            }

                            if ( this._sectorHighlightTl )
                            {
                                this._sectorHighlightTl.kill();
                            }
                            this._sectorHighlightTl = new gsap.core.Timeline();
                            this._sectorHighlightTl.to( this._sectorHighlightOverlay, { alpha: 0.7, repeat: 6, yoyo: true, duration: 0.1, ease: "sine.inOut" } );
                            this._sectorHighlightTl.to( this._sectorHighlightOverlay, { alpha: 0, duration: 0.4, ease: "linear.inOut" }, ">+0.6" );
                            this._sectorHighlightTl.to( this._sectorHighlightOutline, { alpha: 0, duration: 0.4, ease: "linear.inOut" }, "<" );
                            this._sectorHighlightTl.call( () => { this._sectorHighlightWrapper.visible = false; } );
                            //
                            ServiceLocator.game.tweenGroup.add( this._sectorHighlightTl );
                        }

                        private _showScoreAt( dartScoreInfo: IDartScore, position: PIXI.Point ): void
                        {
                            // TEST: 
                            //dartScoreInfo.area = "DOUBLE";

                            /*if ( dartScoreInfo.area == "BULL" )
                            {
                                ServiceLocator.audio.playSfx( "dart_scored_bullseye_inner" );
                            }
                            else if ( dartScoreInfo.area == "OUTER_BULL" )
                            {
                                ServiceLocator.audio.playSfx( "dart_scored_bullseye_outer" );
                            }
                            else if ( dartScoreInfo.area == "MISS" )
                            {
                                ServiceLocator.audio.playSfx( "dart_scored_out" );
                            }
                            else
                            {
                                ServiceLocator.audio.playSfx( "dart_scored" );
                            }*/

                            this._scoreWrapper.visible = true;
                            this._scoreWrapper.position.set( position.x, position.y + this._kScoreWrapperBaseY );

                            if ( this._scoreTl )
                            {
                                this._scoreTl.kill();
                            }
                            this._scoreTl = new gsap.core.Timeline();
                            this._scoreTl.timeScale( 1.4 );
                            //
                            this._scoreBase.y = this._kScoreBgBaseY;
                            this._scoreBase.alpha = 0;
                            //
                            let score: number = dartScoreInfo.score;
                            let isMultBadge = false;
                            if ( dartScoreInfo.area == "DOUBLE" )
                            {
                                isMultBadge = true;
                                this._scoreMultBadge.alpha = 1;
                                this._scoreMultBadge.texture = ServiceLocator.resources.hud_badge_2x.texture;
                                score /= 2;
                            }
                            else if ( dartScoreInfo.area == "TRIPLE" )
                            {
                                isMultBadge = true;
                                this._scoreMultBadge.alpha = 1;
                                this._scoreMultBadge.texture = ServiceLocator.resources.hud_badge_3x.texture
                                score /= 3;
                            }
                            this._scoreText.text = score.toString();
                            this._scoreText.alpha = 0;
                            this._scoreText.scale.set( 3 );
                            // in.
                            this._scoreTl.to( this._scoreBase, { y: 0, duration: 0.4, ease: "back.out" }, 0 );
                            this._scoreTl.to( this._scoreBase, { alpha: 1, duration: 0.2, ease: "linear.inOut" }, 0 );
                            this._scoreTl.to( this._scoreText, { alpha: 1, duration: 0.1, ease: "linear.inOut" }, 0 );
                            this._scoreTl.to( this._scoreText.scale, { x: 1, y: 1, duration: 0.4, ease: "back.out" }, 0 );
                            //
                            let outTime: number = 2 * 1;
                            if ( isMultBadge )
                            {
                                outTime = 0.4;

                                this._scoreMultBadge.scale.set( 1.4 );
                                this._scoreTl.call( () => { 
                                    this._scoreMultBadge.visible = true; 

                                    ServiceLocator.audio.playSfx( "score_mult_bonus" );
                                }, null, outTime );
                                this._scoreTl.to( this._scoreMultBadge.scale, { x: 1, y: 1, duration: 0.4, ease: "back.out" }, outTime );
                                this._scoreTl.to( this._scoreText.scale, { y: 0, duration: 0.1, ease: "sine.out" }, outTime + 0.2 );
                                this._scoreTl.call( () => { this._scoreText.text = dartScoreInfo.score.toString(); }, null, ">" );
                                this._scoreTl.to( this._scoreText.scale, { y: 1, duration: 0.1, ease: "sine.in" }, ">" );

                                outTime += 2 * 1.2;
                            }
                            // out.
                            if ( isMultBadge )
                            {
                                this._scoreTl.to( this._scoreMultBadge, { alpha: 0, duration: 0.4, ease: "linear.inOut" }, outTime );
                                this._scoreTl.call( () => { this._scoreMultBadge.visible = false; }, null );
                            }
                            this._scoreTl.to( this._scoreText, { alpha: 0, duration: 0.4, ease: "linear.inOut" }, outTime );
                            this._scoreTl.to( this._scoreBase, { y: this._scoreBase.y, duration: 0.4, ease: "back.in" }, outTime + 0.2 );
                            this._scoreTl.to( this._scoreBase, { alpha: 0, duration: 0.2, ease: "linear.inOut" }, outTime + 0.4);
                            //
                            this._scoreTl.call( () => { 
                                this._scoreWrapper.visible = false;
                            } );
                            ServiceLocator.game.tweenGroup.add( this._scoreTl );
                        }

                        private _onDartScored( player: Player ): void
                        {
                            this._arrScoredDartWrapper.push( this._dartMoveWrapper );

                            if ( this._gameRulesType == game_rules_type.CLASSIC )
                            {
                                if ( this._playerDartCount == 3 ) 
                                //if ( this._playerDartCount == 1 ) // TEST:
                                {
                                    if ( player == this._arrPlayer[ 1 ] )
                                    {
                                        if ( this._roundCount < 3 )
                                        {
                                            ( this._roundBase.children[ this._roundCount ] as PIXI.Sprite ).texture = ServiceLocator.resources.hud_round_dot_on.texture;
                                        }

                                        this._roundCount += 1;
                                    }

                                    // TEST:
                                    //if ( this._roundCount > 0 )
                                    if ( this._roundCount > 3 )
                                    {
                                        this._showRoundScoreBanner( this._curPlayer, this._curPlayer.score - this._playerRoundScore, () => {
                                            this._state = state_type.GAME_OVER;

                                            let arrPlayer: Array<Player> = this._arrPlayer.slice( 0 );

                                            if ( Session.i.gameType == game_type.CLASSIC_OFFLINE || Session.i.gameType == game_type.POINTS_OFFLINE )
                                            {
                                                if ( this._arrPlayer[ 0 ].score != this._arrPlayer[ 1 ].score )
                                                {
                                                    this._gameOverPopup = new YouWinPopup();
                                                }
                                                else
                                                {
                                                    this._gameOverPopup = new DrawPopup();
                                                }
                                            }
                                            else
                                            {
                                                if ( this._arrPlayer[ this._myPlayer.id ].score > this._arrPlayer[ 1 - this._myPlayer.id ].score )
                                                {
                                                    this._gameOverPopup = new YouWinPopup();
                                                }
                                                else if ( this._arrPlayer[ this._myPlayer.id ].score < this._arrPlayer[ 1 - this._myPlayer.id ].score )
                                                {
                                                    this._gameOverPopup = new YouLosePopup();
                                                }
                                                else
                                                {
                                                    this._gameOverPopup = new DrawPopup();
                                                }
                                                
                                                if ( this._myPlayer.id == 1 )
                                                {
                                                    arrPlayer = [ arrPlayer[ 1 ], arrPlayer[ 0 ] ];
                                                }
                                            }
                                            this._gameOverPopup.players = arrPlayer;
                                            this._gameOverPopup.isRematchRequestReceived = this._isRematchRequestReceived;
                                            this._gameOverPopup.onEnter();
                                            this._gameOverPopup.onClosed.addOnce( ( popup: GameOverPopup, isPlay: boolean ) => {
                                                popup.onLeave();
                                                
                                                if ( Session.i.gameType == game_type.CLASSIC_MATCHMAKING )
                                                {
                                                    this._onCompleted.dispatch( screen_type.LOBBY, { isPlay: isPlay, gameType: game_type.CLASSIC_MATCHMAKING } );
                                                }
                                                else if ( Session.i.gameType == game_type.CLASSIC_PRIVATE )
                                                {
                                                    let isPrivateHost: boolean = !this._arrPlayer[ 0 ].isBot;
                                                    if ( isPrivateHost && isPlay )
                                                    {
                                                        this._isKeepRoom = true;
                                                        for ( let missingPlayerId of this._arrMissingPlayerId )
                                                        {
                                                            if ( missingPlayerId == 0 )
                                                            {
                                                                isPrivateHost = false;
                                                                break;
                                                            }
                                                        }
                                                        if ( isPrivateHost )
                                                        {
                                                            for ( let i: number = Session.i.room.length - 1; i > 0; --i )
                                                            {
                                                                for ( let missingPlayerId of this._arrMissingPlayerId )
                                                                {   
                                                                    if ( i == missingPlayerId )
                                                                    {
                                                                        Session.i.room.splice( i, 1 );
                                                                        break;
                                                                    }
                                                                }
                                                            }
                                                        }
                                                    }
                                                    
                                                    this._onCompleted.dispatch( screen_type.LOBBY, { isPlayPrivate: isPlay, isPrivateHost: isPrivateHost } );
                                                }
                                                else if ( Session.i.gameType == game_type.CLASSIC_OFFLINE )
                                                {
                                                    this._onCompleted.dispatch( screen_type.LOBBY, { isPlayOffline: isPlay, gameType: game_type.CLASSIC_OFFLINE } );
                                                }
                                            } );
                                        } );
                                    }
                                    else
                                    {
                                        this._showRoundScoreBanner( this._curPlayer, this._curPlayer.score - this._playerRoundScore, () => {
                                            if ( Session.i.gameType == game_type.CLASSIC_OFFLINE )
                                            {
                                                let handDevicePopup: HandDevicePopup = new HandDevicePopup();
                                                handDevicePopup.playerIndex = this._myPlayer == this._arrPlayer[ 0 ] ? 1 : 0;
                                                handDevicePopup.onEnter();
                                                handDevicePopup.onClosed.add( () => {
                                                    handDevicePopup.onLeave();

                                                    this._createDart( this._curPlayer.nextPlayer );

                                                    this._nextPlayer();
                                                } );
                                            }
                                            else
                                            {
                                                this._createDart( this._curPlayer.nextPlayer );

                                                this._nextPlayer();
                                            }
                                        } );
                                    }
                                }
                                else
                                {
                                    this._playerInactivityTimer = 0;

                                    this._createDart( this._curPlayer );

                                    this._isNextAction = true;

                                    this._managePlayerTurn( this._curPlayer );
                                }
                            }
                            else
                            {
                                if ( this._curPlayer.score == 0 )
                                //if ( this._curPlayer.score > 0 ) // TEST:
                                {
                                    this._state = state_type.GAME_OVER;

                                    let arrPlayer: Array<Player> = this._arrPlayer.slice( 0 );
                                    
                                    if ( Session.i.gameType == game_type.CLASSIC_OFFLINE || Session.i.gameType == game_type.POINTS_OFFLINE )
                                    {
                                        this._gameOverPopup = new YouWinPopup();
                                    }
                                    else
                                    {
                                        this._gameOverPopup = this._myPlayer == this._curPlayer ? new YouWinPopup() : new YouLosePopup();

                                        if ( this._myPlayer.id == 1 )
                                        {
                                            arrPlayer = [ arrPlayer[ 1 ], arrPlayer[ 0 ] ];
                                        }
                                    }
                                    this._gameOverPopup.players = arrPlayer;
                                    this._gameOverPopup.isRematchRequestReceived = this._isRematchRequestReceived;
                                    this._gameOverPopup.onEnter();
                                    this._gameOverPopup.onClosed.addOnce( ( popup: GameOverPopup, isPlay: boolean ) => {
                                        popup.onLeave();
                                        
                                        if ( Session.i.gameType == game_type.POINTS_MATCHMAKING )
                                        {
                                            this._onCompleted.dispatch( screen_type.LOBBY, { isPlay: isPlay, gameType: game_type.POINTS_MATCHMAKING } );
                                        }
                                        else if ( Session.i.gameType == game_type.POINTS_PRIVATE )
                                        {
                                            let isPrivateHost: boolean = !this._arrPlayer[ 0 ].isBot;
                                            if ( isPrivateHost && isPlay )
                                            {
                                                this._isKeepRoom = true;
                                                for ( let missingPlayerId of this._arrMissingPlayerId )
                                                {
                                                    if ( missingPlayerId == 0 )
                                                    {
                                                        isPrivateHost = false;
                                                        break;
                                                    }
                                                }
                                                if ( isPrivateHost )
                                                {
                                                    for ( let i: number = Session.i.room.length - 1; i > 0; --i )
                                                    {
                                                        for ( let missingPlayerId of this._arrMissingPlayerId )
                                                        {   
                                                            if ( i == missingPlayerId )
                                                            {
                                                                Session.i.room.splice( i, 1 );
                                                                break;
                                                            }
                                                        }
                                                    }
                                                }
                                            }
                                            
                                            this._onCompleted.dispatch( screen_type.LOBBY, { isPlayPrivate: isPlay, isPrivateHost: isPrivateHost } );
                                        }
                                        else if ( Session.i.gameType == game_type.POINTS_OFFLINE )
                                        {
                                            this._onCompleted.dispatch( screen_type.LOBBY, { isPlayOffline: isPlay, gameType: game_type.POINTS_OFFLINE } );
                                        }
                                    } );
                                }
                                else
                                {
                                    if ( this._playerDartCount == 3 || this._curPlayer.isBusted )
                                    {
                                        this._curPlayer.isBusted = false;

                                        if ( player == this._arrPlayer[ 1 ] )
                                        {
                                            this._roundCount += 1;
                                        }

                                        this._showRoundScoreBanner( this._curPlayer, this._curPlayer.score - this._playerRoundScore, () => {
                                            if ( Session.i.gameType == game_type.POINTS_OFFLINE )
                                            {
                                                let handDevicePopup: HandDevicePopup = new HandDevicePopup();
                                                handDevicePopup.playerIndex = this._myPlayer == this._arrPlayer[ 0 ] ? 1 : 0;
                                                handDevicePopup.onEnter();
                                                handDevicePopup.onClosed.add( () => {
                                                    handDevicePopup.onLeave();
                                                    
                                                    this._createDart( this._curPlayer.nextPlayer );
                                                
                                                    this._nextPlayer();

                                                    if ( this._curPlayer.score <= 60 )
                                                    {
                                                        this._showTargetSectorOutline( this._curPlayer.score );
                                                    }
                                                } );
                                            }
                                            else
                                            {
                                                this._createDart( this._curPlayer.nextPlayer );
                                                
                                                this._nextPlayer();

                                                if ( this._curPlayer.score <= 60 )
                                                {
                                                    this._showTargetSectorOutline( this._curPlayer.score );
                                                }
                                            }
                                        } );
                                    }
                                    else
                                    {
                                        this._playerInactivityTimer = 0;

                                        this._createDart( this._curPlayer );

                                        this._isNextAction = true;

                                        this._managePlayerTurn( this._curPlayer );

                                        if ( this._curPlayer.score <= 60 )
                                        {
                                            this._showTargetSectorOutline( this._curPlayer.score );
                                        }
                                    }
                                }
                            }
                        }

                            private _createDart( player: Player ): void
                            {
                                this._dartMoveWrapper = new PIXI3D.Container3D();
                                this._dartMoveWrapper.x = 0;
                                this._dartMoveWrapper.y = this._kDartMoveBaseY;
                                this._dartMoveWrapper.z = this._kDartMoveBaseZ;
                                this._layers.world.addChild( this._dartMoveWrapper );

                                this._dartRotYWrapper = new PIXI3D.Container3D();
                                this._dartMoveWrapper.addChild( this._dartRotYWrapper );

                                const kDartScale: number = this._dartBaseTransformWrapper.scale.x;
                                this._dartBaseTransformWrapper = new PIXI3D.Container3D();
                                this._dartBaseTransformWrapper.z = this._kDartBaseZ;
                                this._dartBaseTransformWrapper.y = this._kDartBaseY;
                                this._dartBaseTransformWrapper.scale.set( kDartScale );
                                this._dartBaseTransformWrapper.rotationQuaternion.setEulerAngles( 45, 0, 0 );
                                this._dartRotYWrapper.addChild( this._dartBaseTransformWrapper );

                                this._dartRotZWrapper = new PIXI3D.Container3D();
                                this._dartBaseTransformWrapper.addChild( this._dartRotZWrapper );
                                
                                // TODO: Create dart according to player model.
                                this._dart = this._dartRotZWrapper.addChild( PIXI3D.Model.from( ( ServiceLocator.resources[ "dart_" + Session.i.room[ player.id ].dartSkinIndex.toString() ] as any ).gltf ) );
                                this._dart.meshes.forEach( ( mesh ) => {
                                    const mat = mesh.material as PIXI3D.StandardMaterial;
                                    let isAltDartSkin: boolean = false;
                                    if ( Session.i.gameType == game_type.CLASSIC_OFFLINE
                                        || Session.i.gameType == game_type.POINTS_OFFLINE )
                                    {
                                        isAltDartSkin = player == this._arrPlayer[ 1 ];
                                    }
                                    else
                                    {
                                        isAltDartSkin = player != this._myPlayer && Session.i.room[ 0 ].dartSkinIndex == Session.i.room[ 1 ].dartSkinIndex;
                                    }
                                    if ( isAltDartSkin )
                                    {
                                        mat.baseColorTexture = ServiceLocator.resources[ "alt_dart_texture_" + Session.i.room[ 0 ].dartSkinIndex.toString() ].texture;
                                        mat.baseColorTexture.uvSet = 1;
                                    }
                                    mat.baseColor = new PIXI3D.Color( 1, 1, 1 ); // Diffuse Color
                                    mat.metallic = 0;                   // Reflectivity
                                    mat.roughness = 10;                  // Surface smoothness
                                } );

                                // Fade in.
                                this._dartMoveWrapper.y -= 4;
                                let tl: gsap.core.Timeline = new gsap.core.Timeline();
                                tl.to( this._dartMoveWrapper, { y: this._kDartMoveBaseY, duration: 0.4, ease: "back.out" } );
                                if ( player == this._myPlayer || Session.i.gameType == game_type.CLASSIC_OFFLINE || Session.i.gameType == game_type.POINTS_OFFLINE )
                                {
                                    tl.call ( () => { this._inputState = input_state_type.IDLE; } );
                                }
                                ServiceLocator.game.tweenGroup.add( tl );
                            }

                            private _findDartScore( target: PIXI3D.Point3D ): IDartScore
                            {
                                let x: number = target.x;
                                let y: number = target.y - this._kDartboardBaseY;

                                // Parámetros de la diana (en mm, estándar)
                                /*const BULL_RADIUS = 40 * this._kPixelToWorldUnits * this._dartboard.scale.x;       // bullseye interior
                                const OUTER_BULL_RADIUS = 81 * this._kPixelToWorldUnits * this._dartboard.scale.x; // bull exterior
                                const TRIPLE_RING_INNER = 305 * this._kPixelToWorldUnits * this._dartboard.scale.x;
                                const TRIPLE_RING_OUTER = 350 * this._kPixelToWorldUnits * this._dartboard.scale.x;
                                const DOUBLE_RING_INNER = 554 * this._kPixelToWorldUnits * this._dartboard.scale.x;
                                const DOUBLE_RING_OUTER = 605 * this._kPixelToWorldUnits * this._dartboard.scale.x;*/
                                const BULL_RADIUS = 38 * this._kPixelToWorldUnits * this._kDartboardScale;       // bullseye interior
                                const OUTER_BULL_RADIUS = 75 * this._kPixelToWorldUnits * this._kDartboardScale; // bull exterior
                                const TRIPLE_RING_INNER = 300 * this._kPixelToWorldUnits * this._kDartboardScale;
                                const TRIPLE_RING_OUTER = 342 * this._kPixelToWorldUnits * this._kDartboardScale;
                                const DOUBLE_RING_INNER = 528 * this._kPixelToWorldUnits * this._kDartboardScale;
                                const DOUBLE_RING_OUTER = 576 * this._kPixelToWorldUnits * this._kDartboardScale;

                                // Secuencia de sectores en sentido horario empezando desde arriba (20 en la parte superior)
                                const sectors = [6, 13, 4, 18, 1, 20, 5, 12, 9, 14, 11, 8, 16, 7, 19, 3, 17, 2, 15, 10];

                                // 1. Calcular radio y ángulo
                                const r = Math.sqrt(x * x + y * y);
                                let theta = Math.atan2(y, x); // en radianes
                                if (theta < 0) theta += 2 * Math.PI; // normalizar a [0, 2π)

                                // 2. Determinar puntuación según el radio
                                if (r <= BULL_RADIUS) return { score: 50, area: "BULL" };
                                if (r <= OUTER_BULL_RADIUS) return { score: 25, area: "OUTER_BULL" };
                                if (r > DOUBLE_RING_OUTER) return { score: 0, area: "MISS" };

                                // 3. Determinar sector según ángulo
                                // Cada sector ocupa 18° = π/10 radianes
                                const sectorAngle = (Math.PI * 2) / 20;
                                // Ajustar para que 20 esté centrado en el eje Y positivo
                                const adjustedAngle = (theta + sectorAngle / 2) % (2 * Math.PI);
                                const sectorIndex = Math.floor(adjustedAngle / sectorAngle);
                                const sectorValue = sectors[sectorIndex];

                                // 4. Determinar si es doble, triple o simple
                                if (r >= TRIPLE_RING_INNER && r <= TRIPLE_RING_OUTER) {
                                    return { score: sectorValue * 3, sector: sectorIndex, area: "TRIPLE" };
                                }
                                if (r >= DOUBLE_RING_INNER && r <= DOUBLE_RING_OUTER) {
                                    return { score: sectorValue * 2, sector: sectorIndex, area: "DOUBLE" };
                                }

                                return { score: sectorValue, sector: sectorIndex, area: r < TRIPLE_RING_OUTER ? "INNER_SINGLE" : "OUTER_SINGLE" };
                            }

                            private _showTargetSectorOutline( targetScore: number ): void
                            {
                                this._targetSectorOutlineWrapper.visible = true;

                                let arrDartScoreInfo: Array<IDartScore> = [];
                                const kSectors: Array<number> = [ 6, 13, 4, 18, 1, 20, 5, 12, 9, 14, 11, 8, 16, 7, 19, 3, 17, 2, 15, 10 ];
                                for ( let i: number = 0; i < kSectors.length; ++i )
                                {
                                    if ( kSectors[ i ] == targetScore )
                                    {
                                        arrDartScoreInfo.push( 
                                            { score: targetScore, sector: i, area: "INNER_SINGLE" },
                                            { score: targetScore, sector: i, area: "OUTER_SINGLE" } );
                                    }
                                    else if ( kSectors[ i ] * 2 == targetScore )
                                    {
                                        arrDartScoreInfo.push( { score: targetScore, sector: i, area: "DOUBLE" } );
                                    }
                                    else if ( kSectors[ i ] * 3 == targetScore )
                                    {
                                        arrDartScoreInfo.push( { score: targetScore, sector: i, area: "TRIPLE" } );
                                    }
                                } 

                                if ( targetScore == 25 )
                                {
                                    arrDartScoreInfo.push( { score: targetScore, area: "OUTER_BULL" } );
                                }
                                else if ( targetScore == 50 )
                                {
                                    arrDartScoreInfo.push( { score: targetScore, area: "BULL" } );
                                }

                                for ( let dartScoreInfo of arrDartScoreInfo )
                                {
                                    const kAngleZ: number = dartScoreInfo.sector === undefined ? 0 : -90 + dartScoreInfo.sector * 18;

                                    let sectorHighlightWrapper: PIXI3D.Container3D = new PIXI3D.Container3D();
                                    sectorHighlightWrapper.rotationQuaternion.setEulerAngles( 0, 0, kAngleZ );
                                    this._targetSectorOutlineWrapper.addChild( sectorHighlightWrapper );   
                                    //
                                    let overlayTextureIndex: number = null;
                                    switch ( dartScoreInfo.area )
                                    {
                                        case "BULL": { overlayTextureIndex = 0; break; }
                                        case "OUTER_BULL": { overlayTextureIndex = 1; break; }
                                        case "INNER_SINGLE": { overlayTextureIndex = 2; break; }
                                        case "TRIPLE": { overlayTextureIndex = 3; break; }
                                        case "OUTER_SINGLE": { overlayTextureIndex = 4; break; }
                                        case "DOUBLE": { overlayTextureIndex = 5; break; }
                                    }
                                    let sectorHighlightOverlay: PIXI3D.Sprite3D = new PIXI3D.Sprite3D( ServiceLocator.resources[ "gp_highlight_" + overlayTextureIndex.toString() ].texture );
                                    sectorHighlightOverlay.alpha = 0.7;
                                    sectorHighlightOverlay.scale.set( this._kDartboardScale );
                                    if ( dartScoreInfo.sector === undefined )
                                    {
                                        sectorHighlightOverlay.anchor.set( 0.5 );
                                    }
                                    else
                                    {
                                        sectorHighlightOverlay.anchor.set( 0.5, 1 );
                                    }
                                    sectorHighlightOverlay.y = [ 0, 0, 75, 297, 338, 528 - 2 ][ overlayTextureIndex ] * sectorHighlightOverlay.scale.x * this._kPixelToWorldUnits;
                                    sectorHighlightWrapper.addChild( sectorHighlightOverlay );    
                                    //
                                    let sectorHighlightOutline: PIXI3D.Sprite3D = new PIXI3D.Sprite3D( ServiceLocator.resources[ "gp_outline_" + overlayTextureIndex.toString() ].texture );
                                    sectorHighlightOutline.scale.set( this._kDartboardScale );
                                    sectorHighlightOutline.anchor.copyFrom( sectorHighlightOverlay.anchor );
                                    sectorHighlightOutline.y = sectorHighlightOverlay.y;
                                    sectorHighlightOutline.z = this._kPixelToWorldUnits;
                                    sectorHighlightWrapper.addChild( sectorHighlightOutline );   
                                }
                            }

        private _onDart_Grabbed( eventData: PIXI.InteractivePointerEvent ): void
        {
            if ( Utils.isDescendant( eventData.target as any, this._settings ) )
            {
                return;
            }

            if ( this._inputState == input_state_type.IDLE )
            {
                //ServiceLocator.audio.playSfx( "dart_grab" );

                if ( this._tutorialHand )
                {
                    if ( this._tutorialHandTl )
                    {
                        this._tutorialHandTl.kill();
                        this._tutorialHandTl = null;
                    }

                    this._tutorialHand.parent.removeChild( this._tutorialHand );
                    this._tutorialHand.destroy();
                    this._tutorialHand = null;
                }

                this._inputState = input_state_type.AIMING;

                this._aimingTimer = 0;

                this._arrThrowFrom.splice( 0 );

                this._root.on( "pointermove", this._onDart_Moved.bind( this ) );

                this._onDart_Moved();
            }
        }

            private _onDart_Moved(): void
            {
                const kCursorPos = ServiceLocator.app.renderer.plugins.interaction.eventData.data.global;
                let ray: PIXI3D.Ray = PIXI3D.Camera.main.screenToRay( kCursorPos.x, kCursorPos.y );
                let p: PIXI3D.Point3D = ray.getPoint( Math.abs( this._dartMoveWrapper.z - PIXI3D.Camera.main.z ) );
                this._dartMoveWrapper.x = p.x;
                this._dartMoveWrapper.y = p.y - this._kDartBaseY;
            }

        private _onDart_Released(): void
        {
            this._root.off( "pointermove" );

            if ( this._inputState != input_state_type.AIMING ) { return; }
            
            if ( this._arrThrowFrom.length > 1 )
            {
                let sampleCount: number = 0;
                let distance: number = 0;
                let time: number = 0;
                let throwUVec: PIXI.Point = new PIXI.Point(); 
                for ( let i: number = this._arrThrowFrom.length - 2; i >= 0; --i )
                {
                    if ( this._arrThrowFrom[ i + 1 ].p.y <= this._arrThrowFrom[ i ].p.y )
                    {
                        sampleCount += 1;

                        throwUVec.x += this._arrThrowFrom[ i + 1 ].p.x - this._arrThrowFrom[ i ].p.x;
                        throwUVec.y += this._arrThrowFrom[ i + 1 ].p.y - this._arrThrowFrom[ i ].p.y;

                        time += this._arrThrowFrom[ i + 1 ].time - this._arrThrowFrom[ i ].time;
                        distance += ( this._arrThrowFrom[ i + 1 ].p.y - this._arrThrowFrom[ i ].p.y ) / ServiceLocator.app.root.scale.x;
                    }
                    else
                    {
                        break;
                    }

                    if ( sampleCount == 2 )
                    {
                        break;
                    }
                }

                //const kThrowDistancePo2: number = Math.pow( this._throwFrom.x - kCursorPos.x, 2 ) + Math.pow( this._throwFrom.y - kCursorPos.y, 2 );
                //console.log( "v=" + distance / time );
                if ( Math.abs( distance ) / time > 500 )
                {
                    this._inputState = input_state_type.WAITING;

                    throwUVec = Utils.findUnitVector( throwUVec );

                    const kCursorPos = ServiceLocator.app.renderer.plugins.interaction.eventData.data.global;
                    let ray: PIXI3D.Ray = PIXI3D.Camera.main.screenToRay( kCursorPos.x + throwUVec.x * 50, kCursorPos.y + throwUVec.y * 50 );
                    let p: PIXI3D.Point3D = ray.getPoint( this._kDarboardDistanceZ + this._kCameraBaseZ );

                    let aimRad: number = Utils.angleBetween( [ 0, -1 ], [ p.x, p.z ] );
                    if ( p.x > 0 )
                    {
                        aimRad *= -1;
                    }
                    //this._dartRotYWrapper.rotationQuaternion.setEulerAngles( 0, aimRad * ( 180 / Math.PI ), 0 );

                    this._throwDart( p, this._curPlayer );

                    this._isNextAction = false;
                }
            }

            if ( this._inputState != input_state_type.WAITING )
            {
                this._inputState = input_state_type.IDLE;

                this._dartMoveWrapper.x = 0;
                this._dartMoveWrapper.y = this._kDartMoveBaseY;
                this._dartMoveWrapper.z = this._kDartMoveBaseZ;
            }
        }

        private _onMultiplayer_EventsReceived( eventsPromise: Promise<LiveEventData[]> ): void
        {
            eventsPromise
                .then( ( arrData: LiveEventData[] ) => {
                    while ( arrData.length > 0  )
                    {
                        let data: LiveEventData = arrData.shift();
                        if ( data.payload !== undefined )
                        {
                            const kPlayerAction: IPlayerAction = JSON.parse( data.payload );
                            if ( kPlayerAction.type == game_event_type.PLAYER_KICKED )
                            {
                                if ( kPlayerAction.playerId == this._myPlayer.id )
                                {
                                    this._onCompleted.dispatch( screen_type.LOBBY, { isKicked: true } );
                                }
                                else
                                {
                                    Session.i.gameEvents.push( data );
                                }
                            }
                            else if ( kPlayerAction.type == game_event_type.DART_THROWN
                                || kPlayerAction.type == game_event_type.TIME_OUT
                                || kPlayerAction.type == game_event_type.IM_ALIVE )
                            {
                                console.log( "_onMultiplayer_EventsReceived :: " + kPlayerAction.type );
                                Session.i.gameEvents.push( data );
                            }
                            else if ( kPlayerAction.type == game_event_type.JOINED_PRIVATE_ROOM
                                || kPlayerAction.type == game_event_type.PRIVATE_GAME_TYPE_UPDATED )
                            {
                                Session.i.roomEvents.push( data );
                            }
                            else if ( kPlayerAction.type == game_event_type.REMATCH_ASKED )
                            {
                                if ( !data.isLocalPlayer )
                                {
                                    if ( this._gameOverPopup )
                                    {
                                        this._gameOverPopup.onRematchAsked();
                                    }
                                    else
                                    {
                                        this._isRematchRequestReceived = true;
                                    }
                                }
                            }
                            else if ( kPlayerAction.type == game_event_type.REMATCH_ACCEPTED )
                            {
                                if ( !data.isLocalPlayer )
                                {
                                    this._gameOverPopup.onRematchAccepted();
                                }
                            }
                        }
                    }
                } );
        }

        private _onMultiplayer_PlayerPresenceUpdated( presenceEventPromise: Promise<LivePlayerDataEvent> ): void
        {
            presenceEventPromise
                .then( ( livePlayerDataEvent: LivePlayerDataEvent ) => {
                    if ( livePlayerDataEvent.action == LivePresenceAction.LEAVE && !livePlayerDataEvent.isLocalPlayer )
                    {
                        for ( let i: number = 0; i < Session.i.room.length; ++i )
                        {
                            if ( Session.i.room[ i ].uid == livePlayerDataEvent.uid )
                            {
                                if ( this._gameOverPopup )
                                {
                                    if ( i == 0 )
                                    {
                                        this._gameOverPopup.onHostLeft();
                                    }
                                    else
                                    {
                                        this._gameOverPopup.onGuestLeft();
                                    }

                                    if ( Session.i.gameType == game_type.CLASSIC_PRIVATE || Session.i.gameType == game_type.POINTS_PRIVATE )
                                    {
                                        Utils.showMessagePopup( "Your opponent left the game.", true );
                                    }
                                }

                                if ( !this._arrPlayer[ i ].isBot )
                                {
                                    this._arrMissingPlayerId.push( this._arrPlayer[ i ].id );
                                }
                                break;
                            }
                        }
                    }
                    else if ( livePlayerDataEvent.action == LivePresenceAction.JOIN )
                    {
                        if ( Session.i.room[ 0 ] == Session.i.profile && !Session.i.profile.isFake && !ServiceLocator.saved.data.isFirstGame )
                        {
                            window.GameSnacks.liveMultiplayer.sendEvent( { payload: JSON.stringify( { type: game_event_type.GAME_ALREADY_STARTED, uid: livePlayerDataEvent.uid } ) } );
                        }
                    }
                } );
        }
        
        private _onProfile_Changed( profile: Profile ): void
        {
            ( Session.i.profile.username as any ) = profile.username;
            ( Session.i.profile.avatar as any ) = profile.avatar;
            if ( Session.i.profile.isFake )
            {
                Session.i.profile.isFake = false;
            } 

            this._myPlayer.name = Session.i.profile.username.prefix;
            this._myPlayer.portraitUrl = Session.i.profile.avatar.src;
            this._myPlayer.updateCharacter();
        }

    public onLeave(): void
    {
        ServiceLocator.multiplayerEventManager.onProfileChange.remove( this._onProfile_Changed, this );

        ServiceLocator.audio.playMusic( "track_0", { loop: true } );

        if ( this._confettiAnim )
        {
            this._confettiAnim.end();
            this._confettiAnim = null;
        }

        for ( let light of PIXI3D.LightingEnvironment.main.lights )
        {
            light.destroy();
        }
        PIXI3D.LightingEnvironment.main.lights = [];

        if ( !Session.i.profile.isFake && !ServiceLocator.saved.data.isFirstGame )
        {
            ServiceLocator.multiplayerEventManager.onEventsReceived.removeAll();
            ServiceLocator.multiplayerEventManager.onPlayerPresenceUpdated.removeAll();
        }

        if ( this._sectorHighlightTl )
        {
            this._sectorHighlightTl.kill();
            this._sectorHighlightTl = null;
        }

        if ( this._throwDartTl )
        {
            this._throwDartTl.kill();
            this._throwDartTl = null;
        }

        if ( this._followDartTl )
        {
            this._followDartTl.kill();
            this._followDartTl = null;
        }

        if ( this._actionTextTl )
        {
            this._actionTextTl.kill();
            this._actionTextTl = null;
        }

        if ( this._tutorialHandTl )
        {
            this._tutorialHandTl.kill();
            this._tutorialHandTl = null;
        }

        /*if ( this._darkLayerTween )
        {
            this._darkLayerTween.kill();
            this._darkLayerTween = null;
        }*/

        if ( this._scoreTl )
        {
            this._scoreTl.kill();
            this._scoreTl = null;
        }

        if ( this._dartboardEntryTl )
        {
            this._dartboardEntryTl.kill();
            this._dartboardEntryTl = null;
        }

        ServiceLocator.game.tweenGroup.reset();

        if ( !this._isKeepRoom && !Session.i.profile.isFake && !ServiceLocator.saved.data.isFirstGame )
        {
            window.GameSnacks.liveMultiplayer.updatePlayerPresence( LivePresenceAction.LEAVE ); 
        }

        for ( let player of this._arrPlayer )
        {
            player.end();
        }
        this._arrPlayer = null;

        if ( Session.i.gameType == game_type.CLASSIC_OFFLINE
            || Session.i.gameType == game_type.POINTS_OFFLINE )
        {
            Session.i.profile = null;
        }

        if ( !this._isKeepRoom )
        {
            Session.i.reset();
        }
        else
        {
            Session.i.gameEvents.splice( 0 );
        }

        this._settings.end();
        this._settings = null;

        if ( this._arrSprayHowl[ 0 ] )
        {
            this._arrSprayHowl[ 0 ].stop();
            this._arrSprayHowl[ 0 ] = null;
        }
        if ( this._arrSprayHowl[ 1 ] )
        {
            this._arrSprayHowl[ 1 ].stop();
            this._arrSprayHowl[ 1 ] = null;
        }

        super.onLeave();
    }

    public onUpdate( dt: number ): void 
    {
        if ( this._state == state_type.GAME_OVER )
        {
            if ( this._gameOverPopup )
            {
                this._gameOverPopup.onUpdate( dt );
            }
            
            return;
        }

        if ( this._bgMaskAnim )
        {
            this._bgMaskAnim.update( dt );
        }
        if ( this._dartboardInnerMaskAnim && this._dartboardInnerMaskAnim.visible )
        {
            this._dartboardInnerMaskAnim.update( dt );
        }
        if ( this._dartboardFrameMaskAnim && this._dartboardFrameMaskAnim.visible )
        {
            this._dartboardFrameMaskAnim.update( dt );
        }
        if ( this._arrUserBaseAnim )
        {
            for ( let userBaseAnim of this._arrUserBaseAnim )
            {
                userBaseAnim.update( dt );
            }
        }
        if ( this._bannerMaskIn.visible )
        {
            this._bannerMaskIn.update( dt );
        }
        if ( this._bannerMaskOut.visible )
        {
            this._bannerMaskOut.update( dt );
        }
        if ( this._confettiAnim )
        {
            this._confettiAnim.update( dt );
        }

        if ( this._isPaused || !this._curPlayer ) { return; }

        if ( this._inputState == input_state_type.AIMING )
        {
            this._aimingTimer += dt;
            
            this._arrThrowFrom.push( { p: ServiceLocator.app.renderer.plugins.interaction.eventData.data.global.clone(), time: this._aimingTimer } );
        }

        if ( this._scoreWrapper.visible )
        {
            const kScreenHit: PIXI.Point = PIXI3D.Camera.main.worldToScreen( this._dartMoveWrapper.x, this._dartMoveWrapper.y, this._dartMoveWrapper.z );
            const kRootHit: PIXI.Point = this._root.toLocal( kScreenHit );
            this._scoreWrapper.position.set( kRootHit.x, kRootHit.y + this._kScoreWrapperBaseY );

            this._scoreWrapper.y -= 30 * ( this._kCameraBaseZ - PIXI3D.Camera.main.z ) / ( this._kCameraBaseZ + Math.abs( this._followDartTargetZ ) );
        }

        if ( this._dartHitAnim.visible )
        {
            this._dartHitAnim.update( dt );
        }

        if ( this._state == state_type.PLAYING )
        {
            if ( this._arrMissingPlayerId.length > 0 )
            {
                this._state = state_type.GAME_OVER;

                let messagePopup: MessagePopup = new MessagePopup();
                messagePopup.message = "Your opponent has left the game.";
                messagePopup.isCloseBtn = true;
                messagePopup.onEnter(); 
                messagePopup.onClosed.add( () => {
                    messagePopup.onLeave();

                    let youWinPopup: YouWinPopup = new YouWinPopup();
                    let arrPlayer: Array<Player> = this._arrPlayer.slice( 0 );
                    if ( this._myPlayer.id == 1 )
                    {
                        arrPlayer = [ arrPlayer[ 1 ], arrPlayer[ 0 ] ];
                    }
                    youWinPopup.players = arrPlayer;
                    youWinPopup.isRematchRequestReceived = false;
                    youWinPopup.onEnter();
                    youWinPopup.onClosed.addOnce( ( popup: GameOverPopup, isPlay: boolean ) => {
                        popup.onLeave();

                        this._onCompleted.dispatch( screen_type.LOBBY, { isPlay: isPlay, gameType: Session.i.gameType } );
                    } );
                    youWinPopup.onHostLeft();
                    //
                    this._gameOverPopup = youWinPopup;
                 } );

                return;
            }

            if ( this._isNextAction )
            {
                let isAction: boolean = false;

                if ( Session.i.gameEvents.length > 0 )
                {
                    const kGameEvent: LiveEventData = Session.i.gameEvents[ 0 ];
                    const kPlayerAction: IPlayerAction = JSON.parse( kGameEvent.payload );
                    //console.assert( this._curPlayer.id == kPlayerAction.playerId, "Player action id [" + kPlayerAction.playerId + "] is different from current player id [" + this._curPlayer.id + "]" );
                    if ( kPlayerAction.playerId == this._myPlayer.id )
                    {
                        Session.i.gameEvents.shift();

                        isAction = true;
                    }
                    else if ( kPlayerAction.playerId == this._curPlayer.id )
                    {
                        Session.i.gameEvents.shift();

                        isAction = true;

                        if ( kPlayerAction.type == game_event_type.DART_THROWN  )
                        {
                            this._throwDart( new PIXI3D.Point3D( ...kPlayerAction.target ), this._curPlayer );

                            this._isNextAction = false;
                        }
                        else if ( kPlayerAction.type == game_event_type.TIME_OUT )
                        {
                            if ( !this._arrPlayer[ kPlayerAction.playerId ].isBot 
                                && !this._arrMissingPlayerId.includes( this._curPlayer.id ) )
                            {
                                this._playerDartCount += 1;

                                ( this._remainingDartsWrapper.children[ this._playerDartCount - 1 ] as PIXI.Sprite ).texture = null;
                                ( this._remainingDartsTextWrapper.children[ 0 ] as PIXI.Text ).text = ( 3 - this._playerDartCount ).toString();

                                this._dart.parent.removeChild( this._dart );
                                this._dart.destroy( { children: true } );
                                this._dart = null;

                                this._onDartScored( this._curPlayer );
                            }
                        }
                        else if ( kPlayerAction.type == game_event_type.PLAYER_KICKED )
                        {
                            if ( !this._arrPlayer[ kPlayerAction.playerId ].isBot 
                                && !this._arrMissingPlayerId.includes( kPlayerAction.playerId ) )
                            {
                                this._arrMissingPlayerId.push( kPlayerAction.playerId );
                            }
                        }
                        else if ( kPlayerAction.type == game_event_type.IM_ALIVE )
                        {
                            this._playerInactivityTimer = kPlayerAction.elapsedTime;
                        }
                    }
                }

                if ( !isAction 
                    && ( !this._throwDartTl || !this._throwDartTl.isActive() )
                    && ( !this._scoreTl || !this._scoreTl.isActive() ) ) 
                {
                    if ( Session.i.gameType == game_type.CLASSIC_MATCHMAKING || Session.i.gameType == game_type.POINTS_MATCHMAKING )
                    {
                        if ( !ServiceLocator.saved.data.isFirstGame || ( this._roundCount > 1 || this._playerDartCount > 0 ) )
                        {
                            this._playerInactivityTimer += dt;
                        }

                        if ( ServiceLocator.saved.data.isFirstGame && !this._tutorialHandTl && this._tutorialHand )
                        {
                            this._tutorialHand.visible = true;

                            this._tutorialHandTl = new gsap.core.Timeline();
                            this._tutorialHandTl.to( this._tutorialHand.scale, { x: 1, y: 1, duration: 0.4, ease: "sine.out" } );
                            this._tutorialHandTl.to( this._tutorialHand, { y: "-=350", duration: 0.4, ease: "sine.in" }, 0.8 );
                            this._tutorialHandTl.to( this._tutorialHand, { alpha: 0, duration: 0.2, ease: "sine.in" }, 1.2 );
                            this._tutorialHandTl.repeat( -1 );
                            this._tutorialHandTl.repeatDelay( 0.6 );
                            ServiceLocator.game.tweenGroup.add( this._tutorialHandTl );
                        }
                    }

                    if ( !this._curPlayer.isBot
                        && ( Session.i.gameType == game_type.CLASSIC_MATCHMAKING || Session.i.gameType == game_type.POINTS_MATCHMAKING )
                        //&& !Session.i.profile.isFake
                        && ( !ServiceLocator.saved.data.isFirstGame || ( this._roundCount > 1 || this._playerDartCount > 0 ) ) )
                    {
                        if ( this._curPlayer == this._myPlayer )
                        {
                            // Auto-play.
                            if ( this._playerInactivityTimer >= this._kPlayerTimeLimit )
                            {
                                this._playerInactivityTimer = 0;

                                this._inputState = input_state_type.DISABLED;

                                if ( !this._arrMissingPlayerId.includes( this._curPlayer.id ) )
                                {
                                    this._playerDartCount += 1;

                                    this._root.off( "pointermove" );

                                    ( this._remainingDartsWrapper.children[ this._playerDartCount - 1 ] as PIXI.Sprite ).texture = null;
                                    ( this._remainingDartsTextWrapper.children[ 0 ] as PIXI.Text ).text = ( 3 - this._playerDartCount ).toString();

                                    this._dart.parent.removeChild( this._dart );
                                    this._dart.destroy( { children: true } );
                                    this._dart = null;

                                    this._onDartScored( this._curPlayer );

                                    if ( !Session.i.profile.isFake )
                                    {
                                        window.GameSnacks.liveMultiplayer.sendEvent( { payload: JSON.stringify( { type: game_event_type.TIME_OUT, playerId: this._curPlayer.id } ) } );
                                    }
                                }
                            }
                        }
                        else
                        {
                            if ( this._playerInactivityTimer >= this._kPlayerInactivityTimeLimit )
                            {
                                this._playerInactivityTimer = 0;

                                if ( !this._arrMissingPlayerId.includes( this._curPlayer.id ) )
                                {
                                    this._arrMissingPlayerId.push( this._curPlayer.id );

                                    if ( !Session.i.profile.isFake )
                                    {
                                        window.GameSnacks.liveMultiplayer.sendEvent( { payload: JSON.stringify( { type: game_event_type.PLAYER_KICKED, playerId: this._curPlayer.id } ) } );
                                    }
                                }
                            }
                        }
                    }
                }
            }

            if ( this._curPlayer == this._myPlayer 
                && ( Session.i.gameType == game_type.CLASSIC_MATCHMAKING || Session.i.gameType == game_type.POINTS_MATCHMAKING )
                //&& !Session.i.profile.isFake
                && ( !ServiceLocator.saved.data.isFirstGame || ( this._roundCount > 1 || this._playerDartCount > 0 ) ) )
            {
                if ( Session.i.inactivityElapsedTime > 0 )
                {
                    this._playerInactivityTimer += Session.i.inactivityElapsedTime;

                    Session.i.inactivityElapsedTime = 0;
                    this._imAliveTimer = 4;
                }

                if ( this._playerInactivityTimer >= this._kPlayerTimeLimit )
                {
                    this._inputState = input_state_type.DISABLED;

                    this._root.off( "pointermove" );
                }

                if ( !Session.i.profile.isFake )
                {
                    this._imAliveTimer += dt;
                    if ( this._imAliveTimer > 4 )
                    {
                        this._imAliveTimer = 0;

                        window.GameSnacks.liveMultiplayer.sendEvent( { payload: JSON.stringify( { type: game_event_type.IM_ALIVE, playerId: this._curPlayer.id, elapsedTime: this._playerInactivityTimer } ) } ); 
                    }
                }
            }
        }

        if ( this._playerInactivityTimer >= this._kPlayerTimeLimit )
        {
            this._curPlayer.setTimeOut( 1, 0 );
        }
        else 
        {
            this._curPlayer.setTimeOut( this._playerInactivityTimer / this._kPlayerTimeLimit, Math.ceil( this._kPlayerTimeLimit - this._playerInactivityTimer ) ); 
        }
    }

    public onTabResumed(): void
    {
        // TODO:
    }

    // #endregion //
}

export interface IGameLayers
{
    bg: PIXI.Container;
    intro: PIXI.Container;
    world: PIXI.Container;
    vfx: PIXI.Container;
    dark: PIXI.Container;
    hud: PIXI.Container;
    top: PIXI.Container;
    inputDisabled: PIXI.Container;
}

interface IPlayerAction
{
    type: game_event_type;
    playerId: number;
    target: Array<number>;
    elapsedTime?: number;
}

const enum state_type
{
    PLAYING = 0,
    GAME_OVER
}

const enum game_rules_type
{
    CLASSIC = 0,
    T301
}

const enum input_state_type
{
    IDLE = 0,
    AIMING,
    WAITING,
    DISABLED
}

interface IDartScore
{
    score: number;
    sector?: number;
    area: "MISS" | "INNER_SINGLE" | "OUTER_SINGLE" | "DOUBLE" | "TRIPLE" | "BULL" | "OUTER_BULL";
}

interface IThrowFrom
{
    p: PIXI.Point;
    time: number;
}